import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { 
  getPatients, getPatient, createConsultation, checkProfileComplete, 
  generateCDSSRecommendations, getPatientLatestScales, saveDraft, 
  getPatientDraft, deletePatientDraft, getFollowUpContext,
  getUsAwvClinicalSummaryPDF, getUsPersonalizedPreventionPlanPDF, getUsPatientCaregiverSummaryPDF,
  getUsDemoDashboard, getUsDemoAwvClinicalSummaryPDF, getUsDemoPersonalizedPreventionPlanPDF, getUsDemoPatientCaregiverSummaryPDF,
  postUsSteadiPreview,
  postUsMoodPreview,
  postUsCognitionPreview,
  postUsFunctionalPreview,
  postUsFrailtyPreview,
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Save, Loader2, Plus, Trash2, AlertTriangle,
  Heart, Activity, FileText, User, Stethoscope, Brain,
  ClipboardList, Pill, Users, Check,
  Clock, CheckCircle2, Scale, ListChecks, Zap, BookOpen,
  X, ChevronDown, ChevronUp, Apple, Footprints, Target,
  Calendar, AlertCircle, Pencil, TrendingDown, TrendingUp, Minus,
  Copy, Printer, GraduationCap
} from "lucide-react";

import { ScalesEvaluationContainer } from "../components/InteractiveScaleCard";
import { 
  detectGeriatricSyndromes,
} from "../lib/clinical-recommendations";
import {
  convertLawtonAnswersForApi,
  convertLawtonScoresToOptIds,
  convertGijonAnswersForApi,
  convertGijonScoresToOptIds,
  interpretPolifarmaciaSimple,
} from "../lib/scales-data";
import { calculateScoreFromAnswers, interpretScale } from "../lib/scaleScoring";
import { isUsClinicalHost, isUsDemoMode as detectUsDemoMode } from "../lib/usClinicalHost";
import {
  nextLatamWorkflowStep,
  previousLatamWorkflowStep,
  resolveLatamWorkflowStep,
  stripLatamCaptureFields,
} from "../lib/vgiVisibleFreeze";
import { useAuth } from "../context/AuthContext";
import { useAuroraTour } from "../hooks/useAuroraTour";
import { TOUR_CONFIG } from "../lib/auroraTourSteps";

// ============ CONSTANTS ============
const CONSULTATION_TYPES = [
  { value: "Primera vez", label: "Primera vez" },
  { value: "Control", label: "Control" },
  { value: "Reconsulta", label: "Reconsulta" }
];

const USA_VISIT_TYPES = [
  { value: "Annual Wellness Visit", label: "Annual Wellness Visit (AWV)" },
  { value: "Geriatric Assessment", label: "Comprehensive Geriatric Visit" },
  { value: "Cognitive Follow-Up", label: "Cognitive Follow-Up" },
  { value: "Fall Risk Visit", label: "Fall Risk Visit" },
];

const NEXT_VISIT_OPTIONS = [
  { value: "1_semana", label: "1 semana", days: 7 },
  { value: "15_dias", label: "15 días", days: 15 },
  { value: "1_mes", label: "1 mes", days: 30 },
  { value: "2_meses", label: "2 meses", days: 60 },
  { value: "3_meses", label: "3 meses", days: 90 }
];

const SCALE_PACKAGES = [
  { id: "basico", name: "Básico geriátrico", scales: ["barthel", "mmse", "moca", "yesavage", "phq9", "gad7", "mna_sf", "morse", "polifarmacia_simple"] },
  { id: "funcional", name: "Funcional completo", scales: ["barthel", "lawton", "katz", "sppb"] },
  { id: "cognitivo", name: "Cognitivo + afectivo", scales: ["mmse", "moca", "mini_cog", "cam", "pfeiffer", "yesavage", "phq9", "gad7"] },
  { id: "caidas", name: "Riesgo caídas", scales: ["morse", "tinetti", "frail", "sarc_f", "tug"] },
  { id: "nutricion", name: "Nutrición", scales: ["mna_sf"] },
  { id: "cuidador", name: "Cuidador y entorno", scales: ["zarit", "gijon"] },
  { id: "completo", name: "Evaluación integral", scales: ["barthel", "lawton", "katz", "sppb", "mmse", "moca", "mini_cog", "cam", "yesavage", "phq9", "gad7", "mna_sf", "morse", "frail", "sarc_f", "norton", "zarit", "tug", "polifarmacia_simple"] }
];

const QUICK_PACKAGE = SCALE_PACKAGES.find((p) => p.id === "funcional")?.scales || ["barthel", "mna_sf", "morse", "mini_cog"];
const STANDARD_PACKAGE = SCALE_PACKAGES.find((p) => p.id === "basico")?.scales || ["barthel", "mmse", "yesavage", "mna_sf", "morse"];
const COMPLETE_PACKAGE = SCALE_PACKAGES.find((p) => p.id === "completo")?.scales || [];

const AVAILABLE_SCALES = [
  { id: "barthel", name: "Índice de Barthel", category: "Funcionalidad", description: "Actividades básicas vida diaria" },
  { id: "lawton", name: "Escala de Lawton", category: "Funcionalidad", description: "Actividades instrumentales" },
  { id: "katz", name: "Índice de Katz", category: "Funcionalidad", description: "Independencia en AVD" },
  { id: "sppb", name: "SPPB (Short Physical Performance Battery)", category: "Funcionalidad / Movilidad", description: "Balance, marcha y silla" },
  { id: "mmse", name: "Mini-Mental (MMSE)", category: "Cognitivo", description: "Estado cognitivo general" },
  { id: "moca", name: "MoCA (Montreal Cognitive Assessment)", category: "Cognitivo", description: "Evaluación cognitiva avanzada (puntaje total)" },
  { id: "mini_cog", name: "Mini-Cog", category: "Cognitivo", description: "Tamizaje cognitivo rápido (recuerdo + reloj)" },
  { id: "cam", name: "CAM (Confusion Assessment Method)", category: "Cognitivo", description: "Detección de delirium (4 criterios)" },
  { id: "pfeiffer", name: "Test de Pfeiffer", category: "Cognitivo", description: "Deterioro cognitivo rápido" },
  { id: "yesavage", name: "Yesavage (GDS-15)", category: "Afectivo", description: "Depresión geriátrica" },
  { id: "phq9", name: "PHQ-9", category: "Afectivo", description: "Tamizaje de depresión (9 ítems)" },
  { id: "gad7", name: "GAD-7", category: "Salud mental / ansiedad", description: "Tamizaje de ansiedad (7 ítems)" },
  { id: "mna_sf", name: "MNA-SF", category: "Nutrición", description: "Estado nutricional" },
  { id: "morse", name: "Escala de Morse", category: "Caídas", description: "Riesgo de caídas" },
  { id: "tinetti", name: "Test de Tinetti", category: "Caídas", description: "Marcha y equilibrio" },
  { id: "tug", name: "TUG (Timed Up and Go)", category: "Caídas", description: "Movilidad y marcha (tiempo en segundos)" },
  { id: "frail", name: "Escala FRAIL", category: "Fragilidad", description: "Screening de fragilidad" },
  { id: "sarc_f", name: "SARC-F", category: "Funcionalidad / Fragilidad", description: "Tamizaje de sarcopenia (5 ítems)" },
  { id: "cfs", name: "Clinical Frailty Scale", category: "Fragilidad", description: "Nivel de fragilidad" },
  { id: "charlson", name: "Índice de Charlson", category: "Comorbilidad", description: "Comorbilidad y pronóstico" },
  { id: "gijon", name: "Escala de Gijón", category: "Social", description: "Riesgo sociofamiliar" },
  { id: "zarit", name: "Zarit (Sobrecarga del cuidador)", category: "Cuidador", description: "Carga del cuidador familiar (12 ítems)" },
  { id: "eva_dolor", name: "Escala EVA", category: "Dolor", description: "Intensidad del dolor" },
  { id: "norton", name: "Escala de Norton", category: "Úlceras", description: "Riesgo de úlceras por presión" },
  {
    id: "polifarmacia_simple",
    name: "Polifarmacia simple",
    category: "Farmacoterapia",
    description: "Recuento de medicamentos y señales opcionales (sin puntuación sumada)",
  },
];

// Common geriatric medications for autocomplete
const COMMON_MEDICATIONS = [
  "Amlodipino", "Atenolol", "Bisoprolol", "Carvedilol", "Enalapril", "Losartán", "Ramipril", "Valsartán",
  "Atorvastatina", "Rosuvastatina", "Simvastatina",
  "Metformina", "Glibenclamida", "Sitagliptina", "Insulina glargina", "Insulina NPH",
  "Levotiroxina", "Omeprazol", "Pantoprazol", "Ranitidina",
  "Aspirina", "Clopidogrel", "Warfarina", "Rivaroxabán", "Apixabán",
  "Furosemida", "Hidroclorotiazida", "Espironolactona",
  "Calcio + Vitamina D", "Vitamina D3", "Hierro", "Ácido fólico",
  "Donepezilo", "Rivastigmina", "Memantina",
  "Sertralina", "Escitalopram", "Mirtazapina", "Duloxetina",
  "Alprazolam", "Lorazepam", "Zolpidem", "Melatonina",
  "Tramadol", "Paracetamol", "Ibuprofeno", "Diclofenaco", "Pregabalina",
  "Allopurinol", "Colchicina", "Metilprednisolona", "Prednisona",
  "Amoxicilina", "Ciprofloxacino", "Trimetoprim/Sulfametoxazol",
  "Salmeterol/Fluticasona", "Tiotropio", "Salbutamol", "Ipratropio",
  "Gabapentina", "Levodopa/Carbidopa", "Pramipexol"
];

// Motivo de consulta quick phrases
const MOTIVO_PHRASES = [
  "Control de enfermedades crónicas", "Evaluación geriátrica integral", "Dolor", "Caídas recientes",
  "Confusión o desorientación", "Deterioro funcional progresivo", "Pérdida de peso / desnutrición",
  "Depresión / cambios de humor", "Revisión de medicamentos", "Alta hospitalaria reciente",
  "Problemas de movilidad", "Incontinencia urinaria", "Dificultad para deglutir",
  "Insomnio", "Pérdida de memoria"
];

// Workflow steps — flujo clínico: 1 consulta = 1 documento
const WORKFLOW_STEPS = [
  { id: 1, key: "datosGenerales",        label: "Datos generales",          icon: User,         description: "Tipo de consulta y datos del paciente" },
  { id: 2, key: "motivoConsulta",       label: "Motivo e historia",       icon: ClipboardList,description: "Motivo principal e historia de la enfermedad actual" },
  { id: 3, key: "antecedentes",         label: "Antecedentes",            icon: FileText,     description: "Antecedentes y medicamentos habituales" },
  { id: 4, key: "examenFisico",         label: "Examen físico",           icon: Stethoscope,  description: "Signos vitales, antropometría y exploración" },
  { id: 5, key: "evaluacionGeriatrica",  label: "Escalas geriátricas",     icon: Scale,        description: "Evaluación geriátrica integral" },
  { id: 6, key: "estudiosComplementarios", label: "Estudios complementarios", icon: FileText, description: "Imagen y otros estudios (opcional)" },
  { id: 7, key: "diagnosticos",          label: "Diagnósticos",             icon: Brain,        description: "Impresión clínica, síndromes y diagnósticos" },
  { id: 8, key: "planNoFarmacologico",   label: "Plan no farmacológico",    icon: Heart,        description: "Nutrición, ejercicio, prevención caídas, cuidador" },
  { id: 9, key: "tratamientoFarmacologico", label: "Tratamiento farmacológico", icon: Pill, description: "Medicamentos indicados" },
  { id: 10, key: "cierreConsulta",       label: "Seguimiento",               icon: CheckCircle2, description: "Próxima cita y generación de documentos" }
];

/** Pasos del flujo LATAM que el usuario puede saltar sin llenar (misma acción que Continuar). */
const OPTIONAL_WORKFLOW_STEP_IDS = new Set([6]);

const US_GUIDED_BLOCKS = [
  { id: "visit_purpose", label: "Visit Purpose", steps: [1, 2] },
  { id: "four_ms", label: "4Ms Framework", steps: [2] },
  { id: "clinical_screening", label: "Clinical Screening", steps: [2, 5] },
  { id: "assessment_plan", label: "Clinical Review & Care Plan", steps: [7, 8, 9] },
  { id: "auto_summary", label: "Auto Clinical Summary", steps: [10] },
];

const emptyMedication = { nombre: "", dosis: "", frecuencia: "", duracion: "", indicacion: "" };
const emptyDiagnosis = { codigo: "", descripcion: "", tipo: "principal" };

function booleanToYesNo(value) {
  return value ? "yes" : "no";
}

function calculateUsFallRisk(fallRisk) {
  const riskFactors = [
    fallRisk?.recent_falls,
    fallRisk?.fear_of_falling,
    fallRisk?.gait_balance_concern,
    fallRisk?.assistive_device_use,
    fallRisk?.home_safety_concern,
    fallRisk?.orthostatic_dizziness_concern,
    fallRisk?.medication_related_fall_concern,
  ];
  const positives = riskFactors.filter(Boolean).length;
  if (fallRisk?.recent_falls && positives >= 3) return "high risk";
  if (positives >= 4) return "high risk";
  if (positives >= 2) return "moderate risk";
  return "low risk";
}

function calculateUsAwvCompliance(formData) {
  const hasText = (v) => String(v || "").trim().length > 0;
  const hasAny = (...vals) => vals.some((v) => hasText(v) || v === true);

  const hraDocumented = hasAny(formData.awv_health_risk_assessment);
  const medicationReviewDocumented = hasAny(formData.awv_medication_review, formData.four_ms_medication);
  const functionalStatusDocumented = hasAny(
    formData.awv_functional_status,
    formData.functional_summary,
    formData.functional_mobility_status !== "independent",
    formData.functional_adl_status !== "independent",
    formData.functional_iadl_status !== "independent"
  );
  const cognitiveScreeningDocumented = hasAny(
    formData.awv_cognitive_screening,
    formData.cognitive_brief_impression,
    formData.cognitive_memory_concern,
    formData.cognitive_orientation_concern,
    formData.cognitive_caregiver_concern
  );
  const depressionScreeningDocumented = hasAny(
    formData.awv_depression_screening,
    formData.depression_brief_impression,
    formData.depression_depressed_mood_concern,
    formData.depression_loss_of_interest_concern
  );
  const fallRiskDocumented = hasAny(
    formData.awv_fall_risk_screening,
    formData.fall_recent_falls,
    formData.fall_fear_of_falling,
    formData.fall_gait_balance_concern,
    formData.fall_assistive_device_use,
    formData.fall_home_safety_concern,
    formData.fall_orthostatic_dizziness_concern,
    formData.fall_medication_related_concern
  );
  const whatMattersDocumented = hasAny(
    formData.four_ms_what_matters,
    formData.what_matters_patient_priorities,
    formData.what_matters_main_goal,
    formData.what_matters_biggest_concern,
    formData.what_matters_shared_priorities
  );
  const advanceCarePlanningDocumented = hasAny(
    formData.what_matters_advance_directive_status,
    formData.what_matters_decision_maker
  );
  const preventionPlanReady = hasAny(formData.awv_preventive_services_plan, formData.assessment_plan);
  const sdohDocumented = hasAny(
    formData.sdoh_food_insecurity,
    formData.sdoh_housing_instability,
    formData.sdoh_transportation_barriers,
    formData.sdoh_utility_difficulties,
    formData.sdoh_safety_or_support_needs,
    formData.valoracion_social
  );

  const itemMap = [
    { key: "hra_documented", label: "Health risk assessment", done: hraDocumented },
    { key: "medication_review_documented", label: "Medication review", done: medicationReviewDocumented },
    { key: "functional_status_documented", label: "Functional status", done: functionalStatusDocumented },
    { key: "cognitive_screening_documented", label: "Cognitive screening", done: cognitiveScreeningDocumented },
    { key: "depression_screening_documented", label: "Depression screening", done: depressionScreeningDocumented },
    { key: "fall_risk_documented", label: "Fall risk", done: fallRiskDocumented },
    { key: "what_matters_documented", label: "What Matters", done: whatMattersDocumented },
    { key: "advance_care_planning_documented", label: "Advance care planning", done: advanceCarePlanningDocumented },
    { key: "prevention_plan_ready", label: "Prevention care plan", done: preventionPlanReady },
    {
      key: "sdoh_documented",
      label: sdohDocumented ? "SDoH factors" : "SDoH factors (pending capture)",
      done: sdohDocumented,
    },
  ];

  const completedItems = itemMap.filter((i) => i.done).map((i) => i.label);
  const missingItems = itemMap.filter((i) => !i.done).map((i) => i.label);
  const completionScore = Math.round((completedItems.length / itemMap.length) * 100);

  return {
    hra_documented: hraDocumented,
    medication_review_documented: medicationReviewDocumented,
    functional_status_documented: functionalStatusDocumented,
    cognitive_screening_documented: cognitiveScreeningDocumented,
    depression_screening_documented: depressionScreeningDocumented,
    fall_risk_documented: fallRiskDocumented,
    what_matters_documented: whatMattersDocumented,
    advance_care_planning_documented: advanceCarePlanningDocumented,
    prevention_plan_ready: preventionPlanReady,
    sdoh_documented: sdohDocumented,
    awv_completion_score: completionScore,
    completed_items: completedItems,
    missing_items: missingItems,
  };
}

function calculateUsMedicationReviewStatus({ medicationCount = 0, fallRiskLevel = "", cognitionStatus = "", frailtyStatus = "" }) {
  const polypharmacyDetected = Number(medicationCount) >= 5;
  const coFall = String(fallRiskLevel || "").toLowerCase() === "high risk" || String(fallRiskLevel || "").toLowerCase() === "moderate risk";
  const coCognition = String(cognitionStatus || "").toLowerCase().includes("concern");
  const coFrailty = String(frailtyStatus || "").toLowerCase().includes("frail");
  const coCount = [coFall, coCognition, coFrailty].filter(Boolean).length;

  if (!polypharmacyDetected) {
    return {
      medication_count: medicationCount,
      polypharmacy_detected: false,
      priority: "routine",
      follow_up_interval: "12 months",
      clinical_reason: `Medication count is ${medicationCount}; no polypharmacy screening threshold is triggered.`,
      recommended_actions: [
        "Continue routine medication reconciliation at preventive visits.",
        "Recheck medication burden if new symptoms or functional changes occur.",
      ],
    };
  }

  let priority = "high";
  let followUp = "6-8 weeks";
  if (medicationCount >= 10 || (medicationCount >= 8 && coCount >= 2)) {
    priority = "urgent";
    followUp = "2-4 weeks";
  } else if (medicationCount >= 8 || coCount >= 1) {
    priority = "high";
    followUp = "4-6 weeks";
  }

  const overlays = [];
  if (coFall) overlays.push("fall-risk concern");
  if (coCognition) overlays.push("cognitive-screening concern");
  if (coFrailty) overlays.push("frailty-screening concern");
  const overlayText = overlays.length ? ` Coexisting ${overlays.join(" / ")} may increase medication-related safety complexity.` : "";

  return {
    medication_count: medicationCount,
    polypharmacy_detected: true,
    priority,
    follow_up_interval: followUp,
    clinical_reason:
      `Medication count is ${medicationCount}, meeting the polypharmacy screening threshold (>=5); consider structured medication review.` +
      overlayText,
    recommended_actions: [
      "Perform a structured medication reconciliation and indication check.",
      "Review high-risk medications in the context of falls, cognition, and frailty screens.",
      "Schedule close follow-up to confirm a safer and simpler medication plan.",
    ],
  };
}

function calculateUsSdohStatus(formData) {
  const screening = {
    food_insecurity: !!formData.sdoh_food_insecurity,
    housing_instability: !!formData.sdoh_housing_instability,
    transportation_barriers: !!formData.sdoh_transportation_barriers,
    utility_difficulties: !!formData.sdoh_utility_difficulties,
    safety_or_support_needs: !!formData.sdoh_safety_or_support_needs,
  };
  const positiveDomains = Object.entries(screening).filter(([, v]) => v).map(([k]) => k);
  const positiveCount = positiveDomains.length;
  const urgentCombo =
    (screening.housing_instability && screening.safety_or_support_needs) ||
    (screening.food_insecurity && screening.utility_difficulties && positiveCount >= 3);

  if (positiveCount === 0) {
    return {
      sdoh_status: "no_positive_screen",
      positive_domains: [],
      clinical_reason: "No positive social-risk domains were identified in this screening set.",
      priority: "routine",
      follow_up_interval: "12 months",
      recommended_actions: [
        "Continue routine SDOH screening at preventive visits.",
        "Repeat social-risk screening if social circumstances change.",
      ],
    };
  }

  let priority = "moderate";
  let followUp = "6-8 weeks";
  if (urgentCombo) {
    priority = "urgent";
    followUp = "2-4 weeks";
  } else if (positiveCount >= 2) {
    priority = "high";
    followUp = "4-6 weeks";
  }
  const labels = {
    food_insecurity: "food insecurity",
    housing_instability: "housing instability",
    transportation_barriers: "transportation barriers",
    utility_difficulties: "utility difficulties",
    safety_or_support_needs: "safety/support needs",
  };
  return {
    sdoh_status: "positive_screen",
    positive_domains: positiveDomains,
    clinical_reason:
      `Support needs identified in ${positiveCount} social domain(s): ${positiveDomains.map((d) => labels[d] || d).join(", ")}. ` +
      "Consider support-focused follow-up and community resource connection.",
    priority,
    follow_up_interval: followUp,
    recommended_actions: [
      "Confirm positive domains with patient/caregiver and document immediate support needs.",
      "Consider referral to social work/care navigation for practical resource linkage.",
      "Set a recommended follow-up interval to reassess social barriers and support uptake.",
    ],
  };
}

function calculateUsAcpStatus(formData) {
  const acp = {
    acp_discussion_completed: !!formData.acp_discussion_completed,
    healthcare_proxy_identified: !!formData.acp_healthcare_proxy_identified,
    advance_directive_on_file: !!formData.acp_advance_directive_on_file,
    goals_of_care_discussed: !!formData.acp_goals_of_care_discussed,
    code_status_discussed: !!formData.acp_code_status_discussed,
    acp_follow_up_needed: !!formData.acp_follow_up_needed,
  };
  const documentedCount = Object.values(acp).slice(0, 5).filter(Boolean).length;
  if (documentedCount === 0) {
    return {
      acp_status: "pending_documentation",
      clinical_reason: "No structured ACP documentation is recorded for this visit.",
      priority: "routine",
      follow_up_interval: "next preventive visit",
      recommended_actions: [
        "Document whether ACP discussion was completed.",
        "Capture proxy identification and directive status when available.",
      ],
    };
  }
  const incompleteCore = acp.acp_discussion_completed && (!acp.healthcare_proxy_identified || !acp.advance_directive_on_file);
  if (acp.acp_follow_up_needed && incompleteCore) {
    return {
      acp_status: "follow_up_needed",
      clinical_reason: "ACP discussion is documented but proxy and/or directive documentation is still incomplete.",
      priority: "high",
      follow_up_interval: "4-6 weeks",
      recommended_actions: [
        "Complete proxy identification and directive documentation.",
        "Schedule recommended ACP follow-up to finalize chart documentation.",
      ],
    };
  }
  if (acp.acp_follow_up_needed) {
    return {
      acp_status: "follow_up_needed",
      clinical_reason: "ACP follow-up was explicitly marked as needed in this visit.",
      priority: "moderate",
      follow_up_interval: "6-8 weeks",
      recommended_actions: [
        "Revisit ACP documentation elements at follow-up.",
        "Confirm updated goals-of-care and code-status discussion records.",
      ],
    };
  }
  if (incompleteCore) {
    return {
      acp_status: "discussion_started_incomplete",
      clinical_reason: "ACP discussion has started, but core documentation remains incomplete.",
      priority: "moderate",
      follow_up_interval: "next visit",
      recommended_actions: [
        "Confirm healthcare proxy and directive status in structured fields.",
        "Consider follow-up to complete ACP documentation.",
      ],
    };
  }
  return {
    acp_status: "documentation_completed",
    clinical_reason: "Core ACP documentation elements are present in this structured visit record.",
    priority: "routine",
    follow_up_interval: "annual review",
    recommended_actions: [
      "Maintain ACP documentation and update after major clinical changes.",
      "Reconfirm proxy and directive status at future wellness visits.",
    ],
  };
}

function buildUsSmartAlerts(formData, medicationCount = 0) {
  const alerts = [];
  const fallLevel = calculateUsFallRisk({
    recent_falls: formData.fall_recent_falls,
    fear_of_falling: formData.fall_fear_of_falling,
    gait_balance_concern: formData.fall_gait_balance_concern,
    assistive_device_use: formData.fall_assistive_device_use,
    home_safety_concern: formData.fall_home_safety_concern,
    orthostatic_dizziness_concern: formData.fall_orthostatic_dizziness_concern,
    medication_related_fall_concern: formData.fall_medication_related_concern,
  });
  if (fallLevel === "high risk") {
    alerts.push("STEADI: Screen -> Assess -> Intervene. Recommend fall prevention plan, home safety evaluation, and close follow-up.");
  } else if (fallLevel === "moderate risk") {
    alerts.push("STEADI: Screen -> Assess -> Intervene. Consider balance and gait assessment with home safety review.");
  } else {
    alerts.push("STEADI: Screen completed. Provide routine fall prevention counseling.");
  }

  if (formData.cognitive_memory_concern || formData.cognitive_orientation_concern || formData.cognitive_caregiver_concern) {
    alerts.push("Cognitive follow-up recommended based on current screening concerns.");
  }
  if (formData.tipo_consulta === "Annual Wellness Visit") {
    alerts.push(
      formData.awv_type === "Subsequent AWV"
        ? "Subsequent AWV documentation completed with preventive care updates."
        : "Initial AWV documentation completed with preventive care baseline."
    );
  }
  if (formData.functional_mobility_status !== "independent" || formData.functional_adl_status !== "independent" || formData.functional_iadl_status !== "independent") {
    alerts.push("Functional support planning recommended.");
  }
  if (medicationCount >= 5) alerts.push("Medication review recommended due to polypharmacy.");
  return alerts;
}

// Higher score = worse (depression, fall risk, frailty, social). Lower = better.
const SCALE_HIGHER_WORSE = new Set([
  "yesavage",
  "phq9",
  "gad7",
  "morse",
  "frail",
  "cfs",
  "sarc_f",
  "gijon",
  "zarit",
  "tug",
  "polifarmacia_simple",
]);
function isScaleWorsened(scaleId, prev, curr) {
  if (prev == null || curr == null) return false;
  const p = Number(prev), c = Number(curr);
  if (SCALE_HIGHER_WORSE.has(scaleId)) return c > p;
  return c < p; // lower is worse for barthel, mmse, mna_sf, lawton, katz, tinetti, norton
}

// Parse diagnostico string (P) [code] desc / (S) [code] desc into diagnoses array
function parseDiagnosticoFromLastConsultation(str) {
  if (!str || typeof str !== "string") return [];
  const lines = str.split("\n").map(s => s.trim()).filter(Boolean);
  return lines.map(line => {
    const m = line.match(/^\(([PS])\)\s*(?:\[([^\]]*)\]\s*)?(.+)$/);
    if (m) {
      return { codigo: (m[2] || "").trim(), descripcion: (m[3] || "").trim(), tipo: m[1] === "P" ? "principal" : "secundario" };
    }
    return { codigo: "", descripcion: line, tipo: "principal" };
  }).filter(d => d.descripcion);
}

// ============ PREVIOUS CONSULTATION PANEL (LATAM reconsulta) ============
const ALERT_LABELS = {
  falls: "Caídas",
  cognitive_impairment: "Deterioro cognitivo",
  frailty: "Fragilidad",
  nutritional_risk: "Riesgo nutricional",
  polypharmacy: "Polifarmacia",
  functional_dependence: "Dependencia funcional",
};

function formatDateEs(dateValue) {
  if (!dateValue) return "—";
  try {
    return new Date(dateValue).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatBinaryScaleValue(value, scaleId) {
  if (scaleId === "cam" || scaleId === "mini_cog") {
    if (value === true || value === 1 || value === "1") return "Positivo";
    if (value === false || value === 0 || value === "0") return "Negativo";
  }
  return value;
}

function PreviousConsultationPanel({ context, formData, currentScales }) {
  const lc = context?.last_consultation;
  if (!lc) {
    return (
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Este paciente no tiene una consulta previa registrada para comparación.
      </div>
    );
  }

  const scaleNameById = AVAILABLE_SCALES.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {});
  const prevEscalas = lc?.escalas && typeof lc.escalas === "object" ? lc.escalas : {};
  const fallbackScales = context?.last_scale_results && typeof context.last_scale_results === "object" ? context.last_scale_results : {};
  const mergedPrevScales = { ...fallbackScales, ...prevEscalas };
  const previousScales = Object.entries(mergedPrevScales)
    .map(([id, raw]) => {
      const score = raw && typeof raw === "object" ? (raw.puntaje ?? raw.value ?? raw.score) : raw;
      if (score === undefined || score === null || score === "") return null;
      return {
        id,
        name: scaleNameById[id] || id,
        score: formatBinaryScaleValue(score, id),
        interpretation: raw && typeof raw === "object" ? raw.interpretacion : "",
        current: currentScales?.[id] ?? null,
      };
    })
    .filter(Boolean);

  const alertsRaw = Array.isArray(lc?.clinical_alerts) ? lc.clinical_alerts : [];
  const previousAlerts = alertsRaw
    .map((alertItem) => {
      if (typeof alertItem === "string") return alertItem;
      return alertItem?.id || alertItem?.key || alertItem?.name || "";
    })
    .filter(Boolean);
  const mappedAlerts = previousAlerts
    .map((alertKey) => ALERT_LABELS[alertKey] || alertKey)
    .filter((value, idx, arr) => arr.indexOf(value) === idx);

  const currentExam = {
    aspecto: formData?.examen_aspecto_general || "",
    cardioResp: [formData?.examen_cardiovascular, formData?.examen_respiratorio].filter(Boolean).join(" | "),
    abdomen: formData?.examen_abdomen || "",
    neuro: formData?.examen_neurologico || "",
    extremidades: formData?.examen_musculoesqueletico || "",
    marcha: formData?.examen_musculoesqueletico || "",
    piel: formData?.examen_piel || "",
    otros: formData?.examen_otros || "",
  };

  const examRows = [
    { label: "Estado general", previous: lc.examen_aspecto_general, current: currentExam.aspecto },
    {
      label: "Cardiopulmonar",
      previous: [lc.examen_cardiovascular, lc.examen_respiratorio].filter(Boolean).join(" | "),
      current: currentExam.cardioResp,
    },
    { label: "Abdomen", previous: lc.examen_abdomen, current: currentExam.abdomen },
    { label: "Neurológico", previous: lc.examen_neurologico, current: currentExam.neuro },
    { label: "Extremidades", previous: lc.examen_musculoesqueletico, current: currentExam.extremidades },
    { label: "Marcha/equilibrio", previous: lc.examen_musculoesqueletico, current: currentExam.marcha },
    { label: "Piel", previous: lc.examen_piel, current: currentExam.piel },
    { label: "Otros hallazgos", previous: lc.examen_otros, current: currentExam.otros },
  ];

  const vitalsRows = [
    {
      label: "Presión arterial",
      previous: lc?.vitals?.sbp && lc?.vitals?.dbp ? `${lc.vitals.sbp}/${lc.vitals.dbp} mmHg` : "",
      current: formData?.vitals?.sbp && formData?.vitals?.dbp ? `${formData.vitals.sbp}/${formData.vitals.dbp} mmHg` : "",
    },
    { label: "Frecuencia cardiaca", previous: lc?.vitals?.hr ? `${lc.vitals.hr} lpm` : "", current: formData?.vitals?.hr ? `${formData.vitals.hr} lpm` : "" },
    { label: "Frecuencia respiratoria", previous: lc?.vitals?.rr ? `${lc.vitals.rr} rpm` : "", current: formData?.vitals?.rr ? `${formData.vitals.rr} rpm` : "" },
    { label: "Temperatura", previous: lc?.vitals?.temp ? `${lc.vitals.temp} °C` : "", current: formData?.vitals?.temp ? `${formData.vitals.temp} °C` : "" },
    { label: "Saturación de oxígeno", previous: lc?.vitals?.spo2 ? `${lc.vitals.spo2}%` : "", current: formData?.vitals?.spo2 ? `${formData.vitals.spo2}%` : "" },
  ];

  const anthropometryRows = [
    { label: "Peso", previous: lc?.vitals?.weight ? `${lc.vitals.weight} kg` : "", current: formData?.vitals?.weight ? `${formData.vitals.weight} kg` : "" },
    { label: "Talla", previous: lc?.vitals?.height ? `${lc.vitals.height} cm` : "", current: formData?.vitals?.height ? `${formData.vitals.height} cm` : "" },
    { label: "IMC", previous: lc?.vitals?.bmi || "", current: formData?.vitals?.bmi || "" },
    {
      label: "Circunferencia abdominal",
      previous: lc?.vitals?.waist_circ ? `${lc.vitals.waist_circ} cm` : "",
      current: formData?.vitals?.waist_circ ? `${formData.vitals.waist_circ} cm` : "",
    },
  ];

  const renderComparisonRows = (rows) => (
    <div className="space-y-2">
      {rows.map((row) => {
        const prev = row.previous || "—";
        const curr = row.current || "—";
        if (prev === "—" && curr === "—") return null;
        return (
          <p key={row.label} className="text-sm text-slate-700">
            <span className="font-medium">{row.label}:</span> {prev} {"\u2192"} Actual: {curr}
          </p>
        );
      })}
    </div>
  );

  return (
    <div className="mb-6 space-y-3">
      <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-sky-600" />
          <p className="text-sm font-semibold text-slate-900">Datos de la consulta anterior</p>
          <Badge variant="outline" className="ml-auto bg-white text-xs">
            {formatDateEs(lc.fecha_consulta || lc.created_at)}
          </Badge>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Resumen previo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p><span className="font-medium">Motivo de consulta:</span> {lc.motivo_consulta || "—"}</p>
          <p><span className="font-medium">Diagnóstico previo:</span> {lc.diagnostico || "—"}</p>
          <p><span className="font-medium">Impresión clínica previa:</span> {lc.impresion_clinica || "—"}</p>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Signos vitales</CardTitle>
        </CardHeader>
        <CardContent>{renderComparisonRows(vitalsRows)}</CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Antropometría</CardTitle>
        </CardHeader>
        <CardContent>{renderComparisonRows(anthropometryRows)}</CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Examen físico</CardTitle>
        </CardHeader>
        <CardContent>{renderComparisonRows(examRows)}</CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Escalas previas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {previousScales.length === 0 ? (
            <p className="text-sm text-slate-500">No se encontraron escalas evaluadas en la consulta anterior.</p>
          ) : (
            previousScales.map((scale) => (
              <p key={scale.id} className="text-sm text-slate-700">
                <span className="font-medium">{scale.name}:</span> {String(scale.score)}
                {scale.interpretation ? ` (${scale.interpretation})` : ""}
                {scale.current !== null && scale.current !== undefined && scale.current !== ""
                  ? ` \u2192 Actual: ${String(formatBinaryScaleValue(scale.current, scale.id))}`
                  : ""}
              </p>
            ))
          )}
        </CardContent>
      </Card>

      {mappedAlerts.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Alertas clínicas previas</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {mappedAlerts.map((alertLabel) => (
              <Badge key={alertLabel} variant="outline" className="bg-amber-50 border-amber-200 text-amber-700">
                {alertLabel}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============ MEDICATION CHIP INPUT COMPONENT ============
function MedicationChipInput({ medications, setMedications, setHasUnsavedChanges }) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const inputRef = useRef(null);

  const handleInput = (val) => {
    setInputValue(val);
    if (val.length >= 2) {
      const filtered = COMMON_MEDICATIONS.filter(m => m.toLowerCase().includes(val.toLowerCase())).slice(0, 7);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const addMedication = (name) => {
    const newMed = { ...emptyMedication, nombre: name };
    setMedications(prev => [...prev, newMed]);
    setHasUnsavedChanges(true);
    setInputValue("");
    setSuggestions([]);
    setExpandedIdx(medications.length); // expand newly added
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === "Tab") && inputValue.trim()) {
      e.preventDefault();
      addMedication(inputValue.trim());
    }
    if (e.key === "Escape") { setSuggestions([]); }
  };

  const removeMed = (idx) => {
    setMedications(prev => prev.filter((_, i) => i !== idx));
    setHasUnsavedChanges(true);
    if (expandedIdx === idx) setExpandedIdx(null);
  };

  const updateMed = (idx, field, value) => {
    setMedications(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  return (
    <div className="space-y-3">
      {/* Chip display */}
      {medications.length > 0 && (
        <div className="space-y-2">
          {medications.map((med, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="flex items-center justify-between px-3 py-2.5">
                <button
                  type="button"
                  className="flex items-center gap-2 flex-1 text-left"
                  onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                >
                  <Pill className="h-3.5 w-3.5 text-sky-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{med.nombre}</span>
                  {med.dosis && <span className="text-xs text-slate-500">{med.dosis}</span>}
                  {med.frecuencia && <span className="text-xs text-slate-400">· {med.frecuencia}</span>}
                  {expandedIdx === idx ? <ChevronUp className="h-3.5 w-3.5 ml-auto text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto text-slate-400" />}
                </button>
                <button
                  type="button"
                  onClick={() => removeMed(idx)}
                  className="ml-2 p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {expandedIdx === idx && (
                <div className="px-3 pb-3 pt-1 bg-slate-50 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Dosis</Label>
                    <Input className="h-7 text-xs mt-0.5" placeholder="Ej: 10mg" value={med.dosis} onChange={e => updateMed(idx, "dosis", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Frecuencia</Label>
                    <Input className="h-7 text-xs mt-0.5" placeholder="Ej: c/12h" value={med.frecuencia} onChange={e => updateMed(idx, "frecuencia", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Duración</Label>
                    <Input className="h-7 text-xs mt-0.5" placeholder="Ej: 30 días" value={med.duracion} onChange={e => updateMed(idx, "duracion", e.target.value)} />
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Indicación</Label>
                    <Input className="h-7 text-xs mt-0.5" placeholder="Indicación clínica" value={med.indicacion} onChange={e => updateMed(idx, "indicacion", e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Autocomplete input */}
      <div className="relative">
        <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-sky-300 focus-within:border-sky-400 transition-all">
          <Plus className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
            placeholder="Escribir medicamento y presionar Enter..."
            value={inputValue}
            onChange={e => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {inputValue && (
            <button type="button" onClick={() => { setInputValue(""); setSuggestions([]); }} className="text-slate-400 hover:text-slate-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {suggestions.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-sky-50 hover:text-sky-700 transition-colors flex items-center gap-2"
                onClick={() => addMedication(s)}
              >
                <Pill className="h-3.5 w-3.5 text-sky-400" />
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400">
        {medications.length === 0 ? "Sin medicamentos agregados" : `${medications.length} medicamento${medications.length !== 1 ? "s" : ""} — haga clic para ver/editar detalles`}
      </p>
    </div>
  );
}

// ============ LIBRARY PANEL for care plan ============
function LibraryPanel({ title, icon: Icon, color, items, onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden ${color.border}`}>
      <button
        type="button"
        className={`w-full flex items-center justify-between px-4 py-3 ${color.bg} transition-colors hover:opacity-90`}
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color.text}`} />
          <span className={`text-sm font-semibold ${color.text}`}>{title}</span>
        </div>
        {open ? <ChevronUp className={`h-4 w-4 ${color.text}`} /> : <ChevronDown className={`h-4 w-4 ${color.text}`} />}
      </button>
      {open && (
        <div className="bg-white divide-y divide-slate-100">
          {items.map((item, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700">{item.name}</p>
                  {item.source && <p className="text-[10px] text-slate-400 mt-0.5">{item.source}</p>}
                  <ul className="mt-2 space-y-0.5">
                    {item.recommendations.slice(0, 5).map((r, ri) => (
                      <li key={ri} className="text-xs text-slate-600 flex items-start gap-1.5">
                        <span className="text-slate-400 flex-shrink-0 mt-0.5">•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                    {item.recommendations.length > 5 && (
                      <li className="text-xs text-slate-400">+{item.recommendations.length - 5} más...</li>
                    )}
                  </ul>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0 h-7 text-xs"
                  onClick={() => onInsert(item.recommendations.join("\n• "))}
                >
                  Usar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NewConsultation() {
  const { patientId: routePatientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = useMemo(() => {
    const fromQuery = new URLSearchParams(location.search).get("patient_id") || "";
    return routePatientId || fromQuery || "";
  }, [routePatientId, location.search]);
  const { user } = useAuth();
  const isUsHost = isUsClinicalHost();
  const freezeLatamScaleCapture = !isUsHost;
  const isUsDemoMode = detectUsDemoMode();
  const isDemoUser = user?.email === "demo@aurorageriatrica.com";
  const demoVisitId = useMemo(() => new URLSearchParams(location.search).get("demo_visit") || "", [location.search]);
  const modoAula = useMemo(
    () => new URLSearchParams(location.search).get("modo") === "aula",
    [location.search]
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);
  const [latestScales, setLatestScales] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedScales, setSelectedScales] = useState(() => [...STANDARD_PACKAGE]);
  const [scaleSelectionMode, setScaleSelectionMode] = useState("estandar");
  const [scaleAnswers, setScaleAnswers] = useState({});
  const [detectedSyndromes, setDetectedSyndromes] = useState([]);
  const [medications, setMedications] = useState([]);
  const [antMedications, setAntMedications] = useState([]); // current patient medications (antecedentes)
  const [diagnoses, setDiagnoses] = useState([{ ...emptyDiagnosis }]);
  const [followUpContext, setFollowUpContext] = useState(null);
  const [antecedentesEditMode, setAntecedentesEditMode] = useState(false);
  const [usSteadiPreview, setUsSteadiPreview] = useState(null);
  const [usSteadiPreviewLoading, setUsSteadiPreviewLoading] = useState(false);
  const [usSteadiPreviewError, setUsSteadiPreviewError] = useState(null);
  const [usMoodPreview, setUsMoodPreview] = useState(null);
  const [usMoodPreviewLoading, setUsMoodPreviewLoading] = useState(false);
  const [usMoodPreviewError, setUsMoodPreviewError] = useState(null);
  const [usCognitionPreview, setUsCognitionPreview] = useState(null);
  const [usCognitionPreviewLoading, setUsCognitionPreviewLoading] = useState(false);
  const [usCognitionPreviewError, setUsCognitionPreviewError] = useState(null);
  const [usFunctionalPreview, setUsFunctionalPreview] = useState(null);
  const [usFunctionalPreviewLoading, setUsFunctionalPreviewLoading] = useState(false);
  const [usFunctionalPreviewError, setUsFunctionalPreviewError] = useState(null);
  const [usFrailtyPreview, setUsFrailtyPreview] = useState(null);
  const [usFrailtyPreviewLoading, setUsFrailtyPreviewLoading] = useState(false);
  const [usFrailtyPreviewError, setUsFrailtyPreviewError] = useState(null);

  const applyScaleSelectionMode = useCallback((modeId) => {
    setScaleSelectionMode(modeId);
    if (modeId === "rapida") setSelectedScales([...QUICK_PACKAGE]);
    if (modeId === "estandar") setSelectedScales([...STANDARD_PACKAGE]);
    if (modeId === "completa") setSelectedScales([...COMPLETE_PACKAGE]);
    if (modeId === "personalizada") setSelectedScales((prev) => [...new Set(prev)]);
    setHasUnsavedChanges(true);
  }, []);

  const inferScaleSelectionMode = useCallback((scales) => {
    const norm = (arr) => [...new Set(arr || [])].sort().join("|");
    const current = norm(scales);
    if (current === norm(QUICK_PACKAGE)) return "rapida";
    if (current === norm(STANDARD_PACKAGE)) return "estandar";
    if (current === norm(COMPLETE_PACKAGE)) return "completa";
    return "personalizada";
  }, []);

  const [formData, setFormData] = useState({
    paciente_id: patientId || "",
    tipo_consulta: "",
    // Step 2: Motivo
    motivo_consulta: "",
    tiempo_evolucion: "",
    historia_enfermedad_actual: "",
    chief_concern: "",
    history_summary: "",
    four_ms_what_matters: "",
    what_matters_patient_priorities: "",
    what_matters_main_goal: "",
    what_matters_biggest_concern: "",
    what_matters_shared_priorities: "",
    what_matters_decision_maker: "",
    what_matters_advance_directive_status: "",
    four_ms_medication: "",
    four_ms_mentation: "",
    four_ms_mobility: "",
    assessment_plan: "",
    awv_health_risk_assessment: "",
    awv_functional_status: "",
    awv_fall_risk_screening: "",
    awv_cognitive_screening: "",
    awv_depression_screening: "",
    awv_medication_review: "",
    awv_preventive_services_plan: "",
    awv_type: "",
    fall_recent_falls: false,
    fall_fear_of_falling: false,
    fall_gait_balance_concern: false,
    fall_assistive_device_use: false,
    fall_home_safety_concern: false,
    fall_orthostatic_dizziness_concern: false,
    fall_medication_related_concern: false,
    cognitive_memory_concern: false,
    cognitive_orientation_concern: false,
    cognitive_caregiver_concern: false,
    cognitive_brief_impression: "",
    cognitive_follow_up_recommended: false,
    depression_depressed_mood_concern: false,
    depression_loss_of_interest_concern: false,
    depression_brief_impression: "",
    depression_follow_up_recommended: false,
    functional_mobility_status: "independent",
    functional_adl_status: "independent",
    functional_iadl_status: "independent",
    functional_summary: "",
    sdoh_food_insecurity: false,
    sdoh_housing_instability: false,
    sdoh_transportation_barriers: false,
    sdoh_utility_difficulties: false,
    sdoh_safety_or_support_needs: false,
    acp_discussion_completed: false,
    acp_healthcare_proxy_identified: false,
    acp_advance_directive_on_file: false,
    acp_goals_of_care_discussed: false,
    acp_code_status_discussed: false,
    acp_follow_up_needed: false,
    // Step 3: Antecedentes
    antecedentes_patologicos: "",
    antecedentes_quirurgicos: "",
    antecedentes_no_patologicos: "",
    antecedentes_familiares: "",
    alergias: "",
    // Step 4: Examen físico
    vitals: { sbp: "", dbp: "", hr: "", rr: "", temp: "", spo2: "", weight: "", height: "", bmi: "", glucose: "", waist_circ: "", calf_circ: "", arm_circ: "", weight_loss: "", notes: "" },
    examen_aspecto_general: "",
    examen_cardiovascular: "",
    examen_respiratorio: "",
    examen_neurologico: "",
    examen_musculoesqueletico: "",
    examen_abdomen: "",
    examen_piel: "",
    examen_otros: "",
    estudios_relevantes: [],
    // Step 5: Evaluación geriátrica (scale_answers via state)
    valoracion_social: "",
    // Step 6: Interpretación
    impresion_clinica: "",
    sindrome_geriatrico: "",
    // Step 7: Diagnóstico
    // Step 8: Plan
    plan_farmacologico_notas: "",
    plan_dieta: "",
    plan_ejercicio: "",
    plan_prevencion_caidas: "",
    plan_estimulacion_cognitiva: "",
    plan_terapia_ocupacional: "",
    plan_cuidador: "",
    plan_otros: "",
    // Step 9
    proxima_cita: "",
    proxima_cita_hora: "09:00",
    escalas: {},
    clinical_alerts: [],
    suggested_diagnoses: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!modoAula) {
          const profileCheck = await checkProfileComplete().catch(() => ({ data: { complete: false } }));
          setProfileComplete(profileCheck?.data?.complete ?? false);
        }

        if (isUsHost && isUsDemoMode) {
          const demoRes = await getUsDemoDashboard();
          const demoData = demoRes?.data ?? {};
          const demoPatients = Array.isArray(demoData?.demo_patients) ? demoData.demo_patients : [];
          const demoVisits = Array.isArray(demoData?.recent_visits) ? demoData.recent_visits : [];
          const mappedPatients = demoPatients.map((p) => {
            const [first = "", ...rest] = String(p?.name || "").split(" ");
            return {
              id: p?.id,
              nombres: first,
              apellidos: rest.join(" "),
              edad: p?.age ?? "",
              diagnostico_principal: p?.primary_case || "",
              alergias: [],
              enfermedades_cronicas: [],
              medicamentos_actuales: [],
            };
          });
          setPatients(mappedPatients);
          const seededVisit =
            demoVisits.find((v) => String(v?.id) === String(demoVisitId)) ||
            demoVisits.find((v) => String(v?.patient_id) === String(patientId)) ||
            demoVisits[0];
          if (seededVisit) {
            const selected = mappedPatients.find((p) => p.id === seededVisit.patient_id) || null;
            setSelectedPatient(selected);
            const fourMs = seededVisit?.four_ms || {};
            const fallRisk = seededVisit?.fall_risk || {};
            const cognitive = seededVisit?.cognitive_screening || {};
            const depression = seededVisit?.depression_screening || {};
            const functional = seededVisit?.functional_status || {};
            const awv = seededVisit?.awv_sections || {};
            const whatMatters = seededVisit?.what_matters || {};
            const sdoh = seededVisit?.sdoh_screening || {};
            const acp = seededVisit?.advance_care_planning || {};
            setFormData((prev) => ({
              ...prev,
              paciente_id: seededVisit.patient_id || prev.paciente_id,
              demo_visit_id: seededVisit.id || prev.demo_visit_id,
              tipo_consulta: seededVisit.visit_type || prev.tipo_consulta,
              motivo_consulta: seededVisit.chief_concern || prev.motivo_consulta,
              chief_concern: seededVisit.chief_concern || prev.chief_concern,
              history_summary: seededVisit.history_summary || prev.history_summary,
              assessment_plan: seededVisit.assessment_plan || prev.assessment_plan,
              four_ms_what_matters: fourMs.what_matters || prev.four_ms_what_matters,
              what_matters_patient_priorities: whatMatters.patient_priorities || prev.what_matters_patient_priorities,
              what_matters_main_goal: whatMatters.main_goal || prev.what_matters_main_goal,
              what_matters_biggest_concern: whatMatters.biggest_concern || prev.what_matters_biggest_concern,
              what_matters_shared_priorities: whatMatters.shared_priorities || prev.what_matters_shared_priorities,
              what_matters_decision_maker: whatMatters.decision_maker || prev.what_matters_decision_maker,
              what_matters_advance_directive_status: whatMatters.advance_directive_status || prev.what_matters_advance_directive_status,
              four_ms_medication: fourMs.medication || prev.four_ms_medication,
              four_ms_mentation: fourMs.mentation || prev.four_ms_mentation,
              four_ms_mobility: fourMs.mobility || prev.four_ms_mobility,
              awv_health_risk_assessment: awv.health_risk_assessment || prev.awv_health_risk_assessment,
              awv_type: seededVisit.awv_type || prev.awv_type,
              awv_functional_status: awv.functional_status || prev.awv_functional_status,
              awv_fall_risk_screening: awv.fall_risk_screening || prev.awv_fall_risk_screening,
              awv_cognitive_screening: awv.cognitive_screening || prev.awv_cognitive_screening,
              awv_depression_screening: awv.depression_screening || prev.awv_depression_screening,
              awv_medication_review: awv.medication_review || prev.awv_medication_review,
              awv_preventive_services_plan: awv.preventive_services_plan || prev.awv_preventive_services_plan,
              fall_recent_falls: !!fallRisk.recent_falls,
              fall_fear_of_falling: !!fallRisk.fear_of_falling,
              fall_gait_balance_concern: !!fallRisk.gait_balance_concern,
              fall_assistive_device_use: !!fallRisk.assistive_device_use,
              fall_home_safety_concern: !!fallRisk.home_safety_concern,
              fall_orthostatic_dizziness_concern: !!fallRisk.orthostatic_dizziness_concern,
              fall_medication_related_concern: !!fallRisk.medication_related_fall_concern,
              cognitive_memory_concern: !!cognitive.memory_concern,
              cognitive_orientation_concern: !!cognitive.orientation_concern,
              cognitive_caregiver_concern: !!cognitive.caregiver_concern,
              cognitive_brief_impression: cognitive.brief_clinical_impression || prev.cognitive_brief_impression,
              cognitive_follow_up_recommended: !!cognitive.follow_up_recommended,
              depression_depressed_mood_concern: !!depression.depressed_mood_concern,
              depression_loss_of_interest_concern: !!depression.loss_of_interest_concern,
              depression_brief_impression: depression.brief_impression || prev.depression_brief_impression,
              depression_follow_up_recommended: !!depression.follow_up_recommended,
              functional_mobility_status: functional.mobility || prev.functional_mobility_status,
              functional_adl_status: functional.adl || prev.functional_adl_status,
              functional_iadl_status: functional.iadl || prev.functional_iadl_status,
              functional_summary: functional.summary || prev.functional_summary,
              sdoh_food_insecurity: !!sdoh.food_insecurity,
              sdoh_housing_instability: !!sdoh.housing_instability,
              sdoh_transportation_barriers: !!sdoh.transportation_barriers,
              sdoh_utility_difficulties: !!sdoh.utility_difficulties,
              sdoh_safety_or_support_needs: !!sdoh.safety_or_support_needs,
              acp_discussion_completed: !!acp.acp_discussion_completed,
              acp_healthcare_proxy_identified: !!acp.healthcare_proxy_identified,
              acp_advance_directive_on_file: !!acp.advance_directive_on_file,
              acp_goals_of_care_discussed: !!acp.goals_of_care_discussed,
              acp_code_status_discussed: !!acp.code_status_discussed,
              acp_follow_up_needed: !!acp.acp_follow_up_needed,
              proxima_cita: seededVisit.proxima_cita || prev.proxima_cita,
              proxima_cita_hora: seededVisit.proxima_cita_hora || prev.proxima_cita_hora,
            }));
            setCurrentStep(10);
          }
          return;
        }

        if (patientId) {
          const [patientRes, ctxRes, scalesRes, draftRes] = await Promise.all([
            getPatient(patientId),
            getFollowUpContext(patientId).catch(() => ({ data: null })),
            getPatientLatestScales(patientId).catch(() => ({ data: {} })),
            getPatientDraft(patientId).catch(() => ({ data: null }))
          ]);
          const patient = patientRes.data;
          const ctx = ctxRes?.data;
          const draft = draftRes?.data;

          setSelectedPatient(patient);
          setFollowUpContext(ctx);
          setLatestScales(scalesRes?.data || {});

          // Longitudinal prefill: use base_history when follow-up candidate and no draft
          const isFollowUpCandidate = ctx && !ctx.is_first_consultation;
          const hasDraft = draft && draft.id;

          if (isFollowUpCandidate && !hasDraft) {
            const bh = ctx.base_history || {};
            const ant = bh.antecedentes || {};
            const lc = ctx.last_consultation || {};
            const sdoh = lc.sdoh_screening || {};
            const acp = lc.advance_care_planning || {};
            const meds = (bh.medicamentos_actuales || []).map(n => ({ ...emptyMedication, nombre: n }));
            setAntMedications(meds.length ? meds : (patient.medicamentos_actuales || []).map(n => ({ ...emptyMedication, nombre: n })));
            const parsedDiag = parseDiagnosticoFromLastConsultation(lc.diagnostico);
            if (parsedDiag.length > 0) setDiagnoses(parsedDiag);
            setFormData(prev => ({
              ...prev,
              paciente_id: patientId,
              alergias: ant.alergias || (patient.alergias || []).join(", "),
              antecedentes_patologicos: ant.patologicos || (patient.enfermedades_cronicas || []).join(", "),
              antecedentes_quirurgicos: ant.quirurgicos || "",
              antecedentes_no_patologicos: ant.no_patologicos || "",
              antecedentes_familiares: ant.familiares || "",
              valoracion_social: lc.valoracion_social || prev.valoracion_social || "",
              sdoh_food_insecurity: !!sdoh.food_insecurity,
              sdoh_housing_instability: !!sdoh.housing_instability,
              sdoh_transportation_barriers: !!sdoh.transportation_barriers,
              sdoh_utility_difficulties: !!sdoh.utility_difficulties,
              sdoh_safety_or_support_needs: !!sdoh.safety_or_support_needs,
              acp_discussion_completed: !!acp.acp_discussion_completed,
              acp_healthcare_proxy_identified: !!acp.healthcare_proxy_identified,
              acp_advance_directive_on_file: !!acp.advance_directive_on_file,
              acp_goals_of_care_discussed: !!acp.goals_of_care_discussed,
              acp_code_status_discussed: !!acp.code_status_discussed,
              acp_follow_up_needed: !!acp.acp_follow_up_needed,
              impresion_clinica: lc.impresion_clinica || prev.impresion_clinica || "",
              sindrome_geriatrico: lc.sindrome_geriatrico || prev.sindrome_geriatrico || ""
            }));
            // Merge last scale results for display
            if (ctx.last_scale_results && Object.keys(ctx.last_scale_results).length) {
              setLatestScales(prev => ({ ...prev, ...ctx.last_scale_results }));
            }
          } else {
            const patMeds = (patient.medicamentos_actuales || []);
            setAntMedications(patMeds.map(n => ({ ...emptyMedication, nombre: n })));
            setFormData(prev => ({
              ...prev,
              paciente_id: patientId,
              alergias: (patient.alergias || []).join(", "),
              antecedentes_patologicos: (patient.enfermedades_cronicas || []).join(", ")
            }));
          }

          if (hasDraft) {
              setDraftId(draft.id);
              setCurrentStep(
                freezeLatamScaleCapture
                  ? resolveLatamWorkflowStep(draft.current_step || 1)
                  : draft.current_step || 1
              );
              setSelectedScales(draft.selected_scales || []);
              setScaleSelectionMode(inferScaleSelectionMode(draft.selected_scales || []));
              const raw = draft.scale_answers || {};
              const converted = Object.fromEntries(
                Object.entries(raw).map(([k, v]) => [k, k === "lawton" ? convertLawtonScoresToOptIds(v) : k === "gijon" ? convertGijonScoresToOptIds(v) : v])
              );
              setScaleAnswers(converted);
              setMedications(draft.medications || []);
              setAntMedications(draft.ant_medications || []);
              setDiagnoses(draft.diagnoses?.length > 0 ? draft.diagnoses : [{ ...emptyDiagnosis }]);
              setFormData(prev => ({ ...prev, ...draft.form_data }));
              setLastSaved(draft.updated_at ? new Date(draft.updated_at) : null);
              toast.info(isUsHost ? "Draft loaded" : "Borrador cargado");
            }
        } else {
          const patientsRes = await getPatients();
          setPatients(patientsRes.data);
        }
      } catch {
        toast.error(isUsHost ? "Unable to load visit workflow data." : "Error al cargar datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [patientId, inferScaleSelectionMode, isUsHost, freezeLatamScaleCapture, isUsDemoMode, demoVisitId, modoAula]);

  const handlePatientSelect = async (id) => {
    setFormData(prev => ({ ...prev, paciente_id: id }));
    try {
      const [patientRes, ctxRes, scalesRes, draftRes] = await Promise.all([
        getPatient(id),
        getFollowUpContext(id).catch(() => ({ data: null })),
        getPatientLatestScales(id).catch(() => ({ data: {} })),
        getPatientDraft(id).catch(() => ({ data: null }))
      ]);
      const patient = patientRes.data;
      const ctx = ctxRes?.data;
      const draft = draftRes?.data;

      setSelectedPatient(patient);
      setFollowUpContext(ctx);
      setLatestScales(scalesRes?.data || {});

      const isFollowUpCandidate = ctx && !ctx.is_first_consultation;
      const hasDraft = draft && draft.id;

      if (isFollowUpCandidate && !hasDraft) {
        const bh = ctx.base_history || {};
        const ant = bh.antecedentes || {};
        const lc = ctx.last_consultation || {};
        const sdoh = lc.sdoh_screening || {};
        const meds = (bh.medicamentos_actuales || []).map(n => ({ ...emptyMedication, nombre: n }));
        setAntMedications(meds.length ? meds : (patient.medicamentos_actuales || []).map(n => ({ ...emptyMedication, nombre: n })));
        const parsedDiag = parseDiagnosticoFromLastConsultation(lc?.diagnostico);
        if (parsedDiag.length > 0) setDiagnoses(parsedDiag);
        setFormData(prev => ({
          ...prev,
          paciente_id: id,
          alergias: ant.alergias || (patient.alergias || []).join(", "),
          antecedentes_patologicos: ant.patologicos || (patient.enfermedades_cronicas || []).join(", "),
          antecedentes_quirurgicos: ant.quirurgicos || "",
          antecedentes_no_patologicos: ant.no_patologicos || "",
          antecedentes_familiares: ant.familiares || "",
          valoracion_social: lc?.valoracion_social || "",
          sdoh_food_insecurity: !!sdoh.food_insecurity,
          sdoh_housing_instability: !!sdoh.housing_instability,
          sdoh_transportation_barriers: !!sdoh.transportation_barriers,
          sdoh_utility_difficulties: !!sdoh.utility_difficulties,
          sdoh_safety_or_support_needs: !!sdoh.safety_or_support_needs,
          impresion_clinica: lc?.impresion_clinica || "",
          sindrome_geriatrico: lc?.sindrome_geriatrico || ""
        }));
        if (ctx.last_scale_results && Object.keys(ctx.last_scale_results).length) {
          setLatestScales(prev => ({ ...prev, ...ctx.last_scale_results }));
        }
      } else {
        setAntMedications((patient.medicamentos_actuales || []).map(n => ({ ...emptyMedication, nombre: n })));
        setFormData(prev => ({
          ...prev,
          alergias: (patient.alergias || []).join(", "),
          antecedentes_patologicos: (patient.enfermedades_cronicas || []).join(", ")
        }));
      }

      if (hasDraft) {
        setDraftId(draft.id);
        setCurrentStep(
          freezeLatamScaleCapture
            ? resolveLatamWorkflowStep(draft.current_step || 1)
            : draft.current_step || 1
        );
        setSelectedScales(draft.selected_scales || []);
        setScaleSelectionMode(inferScaleSelectionMode(draft.selected_scales || []));
        const raw = draft.scale_answers || {};
        const converted = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, k === "lawton" ? convertLawtonScoresToOptIds(v) : k === "gijon" ? convertGijonScoresToOptIds(v) : v]));
        setScaleAnswers(converted);
        setMedications(draft.medications || []);
        setAntMedications(draft.ant_medications || []);
        setDiagnoses(draft.diagnoses?.length > 0 ? draft.diagnoses : [{ ...emptyDiagnosis }]);
        setFormData(prev => ({ ...prev, ...draft.form_data }));
        toast.info(isUsHost ? "Draft found" : "Borrador encontrado");
      }
    } catch { toast.error(isUsHost ? "Unable to load the selected patient profile." : "Error al cargar paciente"); }
  };

  const prepareScaleAnswersForApi = useCallback((raw) => {
    if (!raw || typeof raw !== "object") return raw;
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [
      k,
      k === "lawton" ? convertLawtonAnswersForApi(v) : k === "gijon" ? convertGijonAnswersForApi(v) : v
    ]));
  }, []);

  const handleSaveDraft = useCallback(async (showToast = true) => {
    if (isDemoUser) { return; }
    if (!formData.paciente_id) return;
    setSaving(true);
    try {
      const draftData = {
        paciente_id: formData.paciente_id,
        current_step: currentStep,
        form_data: formData,
        selected_scales: selectedScales,
        scale_answers: prepareScaleAnswersForApi(scaleAnswers),
        medications,
        ant_medications: antMedications,
        diagnoses
      };
      const response = await saveDraft(draftData);
      setDraftId(response.data.id);
      setLastSaved(new Date(response.data.updated_at));
      setHasUnsavedChanges(false);
      if (showToast) toast.success(isUsHost ? "Draft saved" : "Avance guardado");
    } catch { toast.error(isUsHost ? "Unable to save the clinical draft." : "Error al guardar borrador"); } finally { setSaving(false); }
  }, [formData, currentStep, selectedScales, scaleAnswers, medications, antMedications, diagnoses, prepareScaleAnswersForApi, isUsHost, isDemoUser]);

  const medicationCountForSteadi = useMemo(() => {
    const cur = (medications || []).filter((m) => String(m?.nombre || "").trim()).length;
    const ant = (antMedications || []).filter((m) => String(m?.nombre || "").trim()).length;
    return cur > 0 ? cur : ant;
  }, [medications, antMedications]);

  useEffect(() => {
    if (!isUsHost || currentStep !== 10) {
      setUsSteadiPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsSteadiPreviewLoading(true);
      setUsSteadiPreviewError(null);
      const medList = (medications || []).some((m) => String(m?.nombre || "").trim())
        ? medications
        : antMedications;
      const medicamentos_actuales = (medList || [])
        .map((m) => String(m?.nombre || "").trim())
        .filter(Boolean);
      const payload = {
        fall_risk: {
          recent_falls: !!formData.fall_recent_falls,
          fear_of_falling: !!formData.fall_fear_of_falling,
          gait_balance_concern: !!formData.fall_gait_balance_concern,
          assistive_device_use: !!formData.fall_assistive_device_use,
          home_safety_concern: !!formData.fall_home_safety_concern,
          orthostatic_dizziness_concern: !!formData.fall_orthostatic_dizziness_concern,
          medication_related_fall_concern: !!formData.fall_medication_related_concern,
        },
        medicamentos_actuales,
      };
      try {
        const res = await postUsSteadiPreview(payload);
        if (!cancelled) setUsSteadiPreview(res.data);
      } catch {
        if (!cancelled) {
          setUsSteadiPreview(null);
          setUsSteadiPreviewError("Unable to load STEADI care plan preview.");
        }
      } finally {
        if (!cancelled) setUsSteadiPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isUsHost,
    currentStep,
    formData.fall_recent_falls,
    formData.fall_fear_of_falling,
    formData.fall_gait_balance_concern,
    formData.fall_assistive_device_use,
    formData.fall_home_safety_concern,
    formData.fall_orthostatic_dizziness_concern,
    formData.fall_medication_related_concern,
    medications,
    antMedications,
    medicationCountForSteadi,
  ]);

  useEffect(() => {
    if (!isUsHost || currentStep !== 10) {
      setUsCognitionPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsCognitionPreviewLoading(true);
      setUsCognitionPreviewError(null);
      const medList = (medications || []).some((m) => String(m?.nombre || "").trim())
        ? medications
        : antMedications;
      const medicamentos_actuales = (medList || [])
        .map((m) => String(m?.nombre || "").trim())
        .filter(Boolean);
      const payload = {
        cognitive_screening: {
          memory_concern: !!formData.cognitive_memory_concern,
          orientation_concern: !!formData.cognitive_orientation_concern,
          caregiver_concern: !!formData.cognitive_caregiver_concern,
          follow_up_recommended: !!formData.cognitive_follow_up_recommended,
        },
        medicamentos_actuales,
      };
      try {
        const res = await postUsCognitionPreview(payload);
        if (!cancelled) setUsCognitionPreview(res.data);
      } catch {
        if (!cancelled) {
          setUsCognitionPreview(null);
          setUsCognitionPreviewError("Unable to load cognitive screening preview.");
        }
      } finally {
        if (!cancelled) setUsCognitionPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isUsHost,
    currentStep,
    formData.cognitive_memory_concern,
    formData.cognitive_orientation_concern,
    formData.cognitive_caregiver_concern,
    formData.cognitive_follow_up_recommended,
    medications,
    antMedications,
  ]);

  useEffect(() => {
    if (!isUsHost || currentStep !== 10) {
      setUsMoodPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsMoodPreviewLoading(true);
      setUsMoodPreviewError(null);
      const medList = (medications || []).some((m) => String(m?.nombre || "").trim())
        ? medications
        : antMedications;
      const medicamentos_actuales = (medList || [])
        .map((m) => String(m?.nombre || "").trim())
        .filter(Boolean);
      const payload = {
        depression_screening: {
          depressed_mood_concern: !!formData.depression_depressed_mood_concern,
          loss_of_interest_concern: !!formData.depression_loss_of_interest_concern,
          follow_up_recommended: !!formData.depression_follow_up_recommended,
        },
        fall_risk: {
          recent_falls: !!formData.fall_recent_falls,
          fear_of_falling: !!formData.fall_fear_of_falling,
          gait_balance_concern: !!formData.fall_gait_balance_concern,
          assistive_device_use: !!formData.fall_assistive_device_use,
          home_safety_concern: !!formData.fall_home_safety_concern,
          orthostatic_dizziness_concern: !!formData.fall_orthostatic_dizziness_concern,
          medication_related_fall_concern: !!formData.fall_medication_related_concern,
        },
        medicamentos_actuales,
      };
      try {
        const res = await postUsMoodPreview(payload);
        if (!cancelled) setUsMoodPreview(res.data);
      } catch {
        if (!cancelled) {
          setUsMoodPreview(null);
          setUsMoodPreviewError("Unable to load mood screening preview.");
        }
      } finally {
        if (!cancelled) setUsMoodPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isUsHost,
    currentStep,
    formData.depression_depressed_mood_concern,
    formData.depression_loss_of_interest_concern,
    formData.depression_follow_up_recommended,
    formData.fall_recent_falls,
    formData.fall_fear_of_falling,
    formData.fall_gait_balance_concern,
    formData.fall_assistive_device_use,
    formData.fall_home_safety_concern,
    formData.fall_orthostatic_dizziness_concern,
    formData.fall_medication_related_concern,
    medications,
    antMedications,
  ]);

  useEffect(() => {
    if (!isUsHost || currentStep !== 10) {
      setUsFunctionalPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsFunctionalPreviewLoading(true);
      setUsFunctionalPreviewError(null);
      const payload = {
        functional_status: {
          mobility: formData.functional_mobility_status || "independent",
          adl: formData.functional_adl_status || "independent",
          iadl: formData.functional_iadl_status || "independent",
          summary: formData.functional_summary || "",
        },
      };
      try {
        const res = await postUsFunctionalPreview(payload);
        if (!cancelled) setUsFunctionalPreview(res.data);
      } catch {
        if (!cancelled) {
          setUsFunctionalPreview(null);
          setUsFunctionalPreviewError("Unable to load functional screening preview.");
        }
      } finally {
        if (!cancelled) setUsFunctionalPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isUsHost,
    currentStep,
    formData.functional_mobility_status,
    formData.functional_adl_status,
    formData.functional_iadl_status,
    formData.functional_summary,
  ]);

  const handleSaveAndContinue = async () => {
    await handleSaveDraft(false);
    if (currentStep < WORKFLOW_STEPS.length) {
      setCurrentStep((s) => (freezeLatamScaleCapture ? nextLatamWorkflowStep(s) : s + 1));
    }
  };

  const handlePrintStep = () => { window.print(); };

  const updateVitals = (field, value) => {
    const newVitals = { ...formData.vitals, [field]: value };
    if ((field === "weight" || field === "height") && newVitals.weight && newVitals.height) {
      const h = parseFloat(newVitals.height) / 100;
      const w = parseFloat(newVitals.weight);
      if (h > 0 && w > 0) newVitals.bmi = (w / (h * h)).toFixed(1);
    }
    setFormData(prev => ({ ...prev, vitals: newVitals }));
    setHasUnsavedChanges(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const applyUsVisitTemplate = (visitType) => {
    if (!isUsHost) return;
    const templates = {
      "Annual Wellness Visit": {
        awv_type: "Initial AWV",
        chief_concern: "Annual preventive wellness evaluation.",
        history_summary: "Comprehensive annual review of chronic conditions, function, cognition, and prevention priorities.",
        four_ms_what_matters: "Clarify patient goals, care preferences, and quality-of-life priorities.",
        four_ms_medication: "Complete medication reconciliation and deprescribing opportunities.",
        four_ms_mentation: "Screen cognition and mood with follow-up planning.",
        four_ms_mobility: "Evaluate falls, gait stability, and safe mobility supports.",
        assessment_plan: "Update preventive care plan and close care gaps based on AWV findings.",
        awv_health_risk_assessment: "Review cardiometabolic, social, and safety risk domains.",
        awv_functional_status: "Document ADL and IADL baseline with support needs.",
        awv_fall_risk_screening: "Perform structured fall risk screen and mitigation steps.",
        awv_cognitive_screening: "Capture cognition baseline and progression concerns.",
        awv_depression_screening: "Perform brief mood screen and stratify follow-up needs.",
        awv_medication_review: "Reconcile active medications and identify high-risk agents.",
        awv_preventive_services_plan: "Document vaccines, cancer screening, bone health, and sensory follow-up.",
      },
      "Fall Risk Visit": {
        chief_concern: "Focused evaluation for fall risk and mobility safety.",
        history_summary: "Review recent falls, near-falls, dizziness episodes, and mobility barriers.",
        four_ms_mobility: "Assess gait, transfer safety, device use, and home hazard burden.",
        assessment_plan: "Prioritize fall prevention actions, PT referral, and home safety interventions.",
        fall_recent_falls: true,
        fall_gait_balance_concern: true,
      },
      "Cognitive Follow-Up": {
        chief_concern: "Follow-up for cognitive concern and longitudinal mentation tracking.",
        history_summary: "Reassess memory trajectory, orientation changes, caregiver observations, and daily function impact.",
        four_ms_mentation: "Track cognitive changes and shared-caregiver concerns over time.",
        assessment_plan: "Define interval cognitive reassessment and caregiver support actions.",
        cognitive_memory_concern: true,
        cognitive_follow_up_recommended: true,
      },
      "Geriatric Assessment": {
        chief_concern: "Comprehensive geriatric visit for multidomain risk.",
        history_summary: "Integrated review of function, cognition, mood, fall risk, and medication burden.",
        assessment_plan: "Define a multidisciplinary care plan with targeted follow-up milestones.",
      },
    };
    const tpl = templates[visitType];
    if (!tpl) return;
    setFormData((prev) => ({ ...prev, ...tpl, tipo_consulta: visitType }));
    if (visitType === "Annual Wellness Visit") setSelectedScales([...STANDARD_PACKAGE]);
    if (visitType === "Fall Risk Visit") setSelectedScales(["morse", "tinetti", "tug", "frail"]);
    if (visitType === "Cognitive Follow-Up") setSelectedScales(["moca", "mini_cog", "phq9", "yesavage"]);
    setHasUnsavedChanges(true);
  };

  const toggleScale = (scaleId) => {
    setSelectedScales(prev => prev.includes(scaleId) ? prev.filter(s => s !== scaleId) : [...prev, scaleId]);
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    if (!isUsHost || currentStep !== 10) {
      setUsFrailtyPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsFrailtyPreviewLoading(true);
      setUsFrailtyPreviewError(null);
      const frailScore = calculateScoreFromAnswers("frail", scaleAnswers?.frail);
      const payload =
        frailScore !== null && frailScore !== undefined && !Number.isNaN(Number(frailScore))
          ? { escalas: { frail: { puntaje: Number(frailScore) } } }
          : { escalas: {} };
      try {
        const res = await postUsFrailtyPreview(payload);
        if (!cancelled) setUsFrailtyPreview(res.data);
      } catch {
        if (!cancelled) {
          setUsFrailtyPreview(null);
          setUsFrailtyPreviewError("Unable to load FRAIL screening preview.");
        }
      } finally {
        if (!cancelled) setUsFrailtyPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isUsHost, currentStep, scaleAnswers]);

  const addDiagnosis = () => { setDiagnoses(d => [...d, { ...emptyDiagnosis }]); setHasUnsavedChanges(true); };
  const removeDiagnosis = (i) => { setDiagnoses(d => d.filter((_, idx) => idx !== i)); setHasUnsavedChanges(true); };
  const updateDiagnosis = (i, field, val) => { setDiagnoses(d => { const u = [...d]; u[i] = { ...u[i], [field]: val }; return u; }); setHasUnsavedChanges(true); };

  const openUsVisitPdf = async (consultationId, reportType) => {
    if (!consultationId || !reportType) return;
    const loaders = {
      clinical_summary: isUsDemoMode ? getUsDemoAwvClinicalSummaryPDF : getUsAwvClinicalSummaryPDF,
      prevention_plan: isUsDemoMode ? getUsDemoPersonalizedPreventionPlanPDF : getUsPersonalizedPreventionPlanPDF,
      caregiver_summary: isUsDemoMode ? getUsDemoPatientCaregiverSummaryPDF : getUsPatientCaregiverSummaryPDF,
    };
    const loader = loaders[reportType];
    if (!loader) return;
    try {
      const response = await loader(consultationId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Unable to generate selected document.");
    }
  };

  const handleSubmit = async (reportToGenerate = null) => {
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para guardar consultas reales."); return; }
    if (isUsHost && isUsDemoMode) {
      if (reportToGenerate) {
        const targetDemoVisitId = demoVisitId || formData?.demo_visit_id || "";
        if (!targetDemoVisitId) {
          toast.error("No demo visit is selected.");
          return;
        }
        await openUsVisitPdf(targetDemoVisitId, reportToGenerate);
        return;
      }
      toast.info("Demo visit is read-only. Use document actions to generate demo reports.");
      return;
    }

    // Guard: patient required
    if (!formData.paciente_id) {
      toast.error(isUsHost ? "Select a patient before saving." : "Seleccione un paciente antes de guardar.");
      return;
    }
    // Guard: prevent double-submit
    if (saving) return;

    setSaving(true);
    const targetPatientId = formData.paciente_id; // capture early so we have it for navigation

    try {
      // ── Build vitals ──────────────────────────────────────────────────────
      const vitalsToSave = {};
      const INT_VITALS = ["sbp","dbp","hr","rr","spo2"];
      const FLOAT_VITALS = ["temp","weight","height","bmi","glucose","waist_circ","calf_circ","arm_circ","weight_loss"];
      Object.entries(formData.vitals || {}).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          vitalsToSave[k] = INT_VITALS.includes(k) ? parseInt(v) : FLOAT_VITALS.includes(k) ? parseFloat(v) : v;
        }
      });

      // ── Build scales ──────────────────────────────────────────────────────
      const escalasObj = {};
      (selectedScales || []).forEach(scaleId => {
        try {
          const score = calculateScoreFromAnswers(scaleId, scaleAnswers[scaleId]);
          if (score !== null && score !== undefined) {
            const row = { value: score, interpretacion: interpretScale(scaleId, score) };
            if (scaleId === "polifarmacia_simple" && scaleAnswers.polifarmacia_simple && typeof scaleAnswers.polifarmacia_simple === "object") {
              const pa = scaleAnswers.polifarmacia_simple;
              row.respuestas = {
                num_medicamentos: pa.num_medicamentos,
                duplicados: !!pa.duplicados,
                potencialmente_inapropiados: !!pa.potencialmente_inapropiados,
                dificultad_adherencia: !!pa.dificultad_adherencia,
              };
              row.interpretacion = interpretPolifarmaciaSimple(score, {
                duplicados: !!pa.duplicados,
                potencialmente_inapropiados: !!pa.potencialmente_inapropiados,
                dificultad_adherencia: !!pa.dificultad_adherencia,
              }).text;
            }
            if (scaleId === "zarit" && scaleAnswers.zarit && typeof scaleAnswers.zarit === "object") {
              row.respuestas = { ...scaleAnswers.zarit };
            }
            if (scaleId === "phq9" && scaleAnswers.phq9 && typeof scaleAnswers.phq9 === "object") {
              row.respuestas = { ...scaleAnswers.phq9 };
            }
            if (scaleId === "gad7" && scaleAnswers.gad7 && typeof scaleAnswers.gad7 === "object") {
              row.respuestas = { ...scaleAnswers.gad7 };
            }
            if (scaleId === "tug" && scaleAnswers.tug?.seconds !== undefined && scaleAnswers.tug?.seconds !== "") {
              row.respuestas = { seconds: scaleAnswers.tug.seconds };
            }
            if (scaleId === "moca" && scaleAnswers.moca?.score !== undefined && scaleAnswers.moca?.score !== "") {
              row.respuestas = { score: scaleAnswers.moca.score };
            }
            if (scaleId === "mini_cog" && scaleAnswers.mini_cog && typeof scaleAnswers.mini_cog === "object") {
              row.respuestas = { ...scaleAnswers.mini_cog };
            }
            if (scaleId === "cam" && scaleAnswers.cam && typeof scaleAnswers.cam === "object") {
              row.respuestas = { ...scaleAnswers.cam };
            }
            if (scaleId === "charlson" && scaleAnswers.charlson && typeof scaleAnswers.charlson === "object") {
              row.respuestas = { ...scaleAnswers.charlson };
            }
            if (scaleId === "eva_dolor" && scaleAnswers.eva_dolor?.score !== undefined && scaleAnswers.eva_dolor?.score !== "") {
              row.respuestas = { score: scaleAnswers.eva_dolor.score };
            }
            if (scaleId === "sarc_f" && scaleAnswers.sarc_f && typeof scaleAnswers.sarc_f === "object") {
              row.respuestas = { ...scaleAnswers.sarc_f };
            }
            if (scaleId === "sppb" && scaleAnswers.sppb && typeof scaleAnswers.sppb === "object") {
              const b = Number(scaleAnswers.sppb.balance);
              const balance = Number.isNaN(b) ? null : Math.max(0, Math.min(4, b));
              const gaitUnable = !!scaleAnswers.sppb.marcha_no_puede;
              const gaitSec = parseFloat(String(scaleAnswers.sppb.marcha_segundos ?? "").replace(",", "."));
              const gaitScore = gaitUnable ? 0 : Number.isNaN(gaitSec) || gaitSec <= 0 ? null : gaitSec <= 4.82 ? 4 : gaitSec <= 6.2 ? 3 : gaitSec <= 8.7 ? 2 : 1;
              const chairUnable = !!scaleAnswers.sppb.silla_no_puede;
              const chairSec = parseFloat(String(scaleAnswers.sppb.silla_segundos ?? "").replace(",", "."));
              const chairScore = chairUnable ? 0 : Number.isNaN(chairSec) || chairSec <= 0 ? null : chairSec <= 11.19 ? 4 : chairSec <= 13.69 ? 3 : chairSec <= 16.69 ? 2 : 1;
              row.respuestas = {
                balance,
                marcha_segundos: gaitUnable ? null : scaleAnswers.sppb.marcha_segundos,
                marcha_score: gaitScore,
                silla_segundos: chairUnable ? null : scaleAnswers.sppb.silla_segundos,
                silla_score: chairScore,
              };
            }
            escalasObj[scaleId] = row;
          }
        } catch { /* skip single broken scale */ }
      });
      Object.entries(formData.escalas || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && !escalasObj[k]) escalasObj[k] = v;
      });

      // ── Build exam text ───────────────────────────────────────────────────
      const examenClinico = [
        formData.examen_aspecto_general    && `Aspecto general: ${formData.examen_aspecto_general}`,
        formData.examen_cardiovascular     && `Cardiovascular: ${formData.examen_cardiovascular}`,
        formData.examen_respiratorio       && `Respiratorio: ${formData.examen_respiratorio}`,
        formData.examen_neurologico        && `Neurológico: ${formData.examen_neurologico}`,
        formData.examen_musculoesqueletico && `Musculoesquelético: ${formData.examen_musculoesqueletico}`,
        formData.examen_abdomen            && `Abdomen: ${formData.examen_abdomen}`,
        formData.examen_piel               && `Piel: ${formData.examen_piel}`,
        formData.examen_otros              && `Otros: ${formData.examen_otros}`
      ].filter(Boolean).join("\n\n");

      // ── Build plan text ───────────────────────────────────────────────────
      const planTerapeutico = [
        formData.plan_farmacologico_notas  && `Plan farmacológico: ${formData.plan_farmacologico_notas}`,
        formData.plan_dieta                && `Nutrición: ${formData.plan_dieta}`,
        formData.plan_ejercicio            && `Ejercicio físico: ${formData.plan_ejercicio}`,
        formData.plan_prevencion_caidas    && `Prevención de caídas: ${formData.plan_prevencion_caidas}`,
        formData.plan_estimulacion_cognitiva && `Estimulación cognitiva: ${formData.plan_estimulacion_cognitiva}`,
        formData.plan_terapia_ocupacional  && `Terapia ocupacional: ${formData.plan_terapia_ocupacional}`,
        formData.plan_cuidador             && `Guía para el cuidador: ${formData.plan_cuidador}`,
        formData.plan_otros                && `Otros: ${formData.plan_otros}`
      ].filter(Boolean).join("\n\n");

      const fallRiskDetails = {
        recent_falls: !!formData.fall_recent_falls,
        fear_of_falling: !!formData.fall_fear_of_falling,
        gait_balance_concern: !!formData.fall_gait_balance_concern,
        assistive_device_use: !!formData.fall_assistive_device_use,
        home_safety_concern: !!formData.fall_home_safety_concern,
        orthostatic_dizziness_concern: !!formData.fall_orthostatic_dizziness_concern,
        medication_related_fall_concern: !!formData.fall_medication_related_concern,
      };
      const fallRiskSummary = calculateUsFallRisk(fallRiskDetails);
      const awvCompliance = calculateUsAwvCompliance(formData);
      const sdohScreening = {
        food_insecurity: !!formData.sdoh_food_insecurity,
        housing_instability: !!formData.sdoh_housing_instability,
        transportation_barriers: !!formData.sdoh_transportation_barriers,
        utility_difficulties: !!formData.sdoh_utility_difficulties,
        safety_or_support_needs: !!formData.sdoh_safety_or_support_needs,
      };

      // ── Build consultation payload ────────────────────────────────────────
      let consultationData = {
        paciente_id:              targetPatientId,
        tipo_consulta:            formData.tipo_consulta || (isUsHost ? "Geriatric Assessment" : "Control"),
        motivo_consulta:          formData.motivo_consulta || formData.chief_concern || "",
        tiempo_evolucion:         formData.tiempo_evolucion || "",
        historia_enfermedad_actual: formData.historia_enfermedad_actual || formData.history_summary || "",
        chief_concern: formData.chief_concern || "",
        history_summary: formData.history_summary || "",
        four_ms: {
          what_matters: formData.four_ms_what_matters || "",
          medication: formData.four_ms_medication || "",
          mentation: formData.four_ms_mentation || "",
          mobility: formData.four_ms_mobility || "",
        },
        what_matters: {
          patient_priorities: formData.what_matters_patient_priorities || "",
          main_goal: formData.what_matters_main_goal || "",
          biggest_concern: formData.what_matters_biggest_concern || "",
          shared_priorities: formData.what_matters_shared_priorities || "",
          decision_maker: formData.what_matters_decision_maker || "",
          advance_directive_status: formData.what_matters_advance_directive_status || "",
        },
        assessment_plan: formData.assessment_plan || "",
        awv_sections: {
          health_risk_assessment: formData.awv_health_risk_assessment || "",
          awv_type: formData.awv_type || "",
          functional_status: formData.awv_functional_status || "",
          fall_risk_screening: formData.awv_fall_risk_screening || "",
          cognitive_screening: formData.awv_cognitive_screening || "",
          depression_screening: formData.awv_depression_screening || "",
          medication_review: formData.awv_medication_review || "",
          preventive_services_plan: formData.awv_preventive_services_plan || "",
        },
        awv_compliance: awvCompliance,
        fall_risk: {
          ...fallRiskDetails,
          risk_level: fallRiskSummary,
        },
        cognitive_screening: {
          memory_concern: !!formData.cognitive_memory_concern,
          orientation_concern: !!formData.cognitive_orientation_concern,
          caregiver_concern: !!formData.cognitive_caregiver_concern,
          brief_clinical_impression: formData.cognitive_brief_impression || "",
          follow_up_recommended: !!formData.cognitive_follow_up_recommended,
          follow_up_recommended_label: booleanToYesNo(!!formData.cognitive_follow_up_recommended),
        },
        depression_screening: {
          depressed_mood_concern: !!formData.depression_depressed_mood_concern,
          loss_of_interest_concern: !!formData.depression_loss_of_interest_concern,
          brief_impression: formData.depression_brief_impression || "",
          follow_up_recommended: !!formData.depression_follow_up_recommended,
          follow_up_recommended_label: booleanToYesNo(!!formData.depression_follow_up_recommended),
        },
        functional_status: {
          mobility: formData.functional_mobility_status || "independent",
          adl: formData.functional_adl_status || "independent",
          iadl: formData.functional_iadl_status || "independent",
          summary: formData.functional_summary || "",
        },
        sdoh_screening: sdohScreening,
        advance_care_planning: {
          acp_discussion_completed: !!formData.acp_discussion_completed,
          healthcare_proxy_identified: !!formData.acp_healthcare_proxy_identified,
          advance_directive_on_file: !!formData.acp_advance_directive_on_file,
          goals_of_care_discussed: !!formData.acp_goals_of_care_discussed,
          code_status_discussed: !!formData.acp_code_status_discussed,
          acp_follow_up_needed: !!formData.acp_follow_up_needed,
        },
        antecedentes: {
          patologicos:   formData.antecedentes_patologicos   || "",
          quirurgicos:   formData.antecedentes_quirurgicos   || "",
          no_patologicos: formData.antecedentes_no_patologicos || "",
          familiares:    formData.antecedentes_familiares    || "",
          alergias:      formData.alergias                   || ""
        },
        examen_clinico:    examenClinico,
        estudios_relevantes: (formData.estudios_relevantes || []).filter(e => e.tipo_estudio || e.resultado),
        valoracion_social: formData.valoracion_social  || "",
        impresion_clinica: formData.impresion_clinica  || "",
        sindrome_geriatrico: formData.sindrome_geriatrico || "",
        diagnostico: (diagnoses || [])
          .filter(d => d.descripcion && d.descripcion.trim())
          .map(d => `${d.tipo === "principal" ? "(P)" : "(S)"} ${d.codigo ? `[${d.codigo}]` : ""} ${d.descripcion}`)
          .join("\n"),
        medicamentos_indicados: (medications || []).filter(m => m.nombre && m.nombre.trim()),
        medicamentos_actuales: (antMedications || []).filter(m => m.nombre && m.nombre.trim()).map(m => m.nombre),
        plan_terapeutico:  planTerapeutico,
        nutrition_plan: formData.plan_dieta || "",
        non_pharm_plan: [
          formData.plan_ejercicio,
          formData.plan_prevencion_caidas,
          formData.plan_estimulacion_cognitiva,
          formData.plan_terapia_ocupacional,
          formData.plan_cuidador,
          formData.plan_otros,
        ]
          .filter(Boolean)
          .join("\n\n"),
        plan_dieta: formData.plan_dieta || "",
        plan_ejercicio: formData.plan_ejercicio || "",
        plan_prevencion_caidas: formData.plan_prevencion_caidas || "",
        plan_estimulacion_cognitiva: formData.plan_estimulacion_cognitiva || "",
        plan_cuidador: formData.plan_cuidador || "",
        plan_otros: formData.plan_otros || "",
        recomendaciones:   formData.plan_cuidador || "",
        escalas:           Object.keys(escalasObj).length > 0 ? escalasObj : {},
        selected_scales:   selectedScales || [],
        scale_answers:     prepareScaleAnswersForApi(scaleAnswers),
        vitals:            Object.keys(vitalsToSave).length > 0 ? vitalsToSave : null,
        clinical_alerts:   formData.clinical_alerts  || [],
        suggested_diagnoses: formData.suggested_diagnoses || [],
        proxima_cita:      formData.proxima_cita      || null,
        proxima_cita_hora: formData.proxima_cita_hora || ""
      };

      // (Dev) Log payload for debugging save failures
      if (freezeLatamScaleCapture) {
        consultationData = stripLatamCaptureFields(consultationData);
      }

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.debug("[NewConsultation] Saving payload:", JSON.stringify(consultationData, null, 2).slice(0, 1200));
      }

      // ── Save consultation ─────────────────────────────────────────────────
      const response = await createConsultation(consultationData);
      const savedConsultation = response?.data;

      // Clean up draft (non-blocking)
      try { await deletePatientDraft(targetPatientId); } catch { /* ok */ }

      // Success feedback
      toast.success(isUsHost ? "Visit saved successfully" : "Consulta guardada correctamente");

      // ── Navegación posterior al guardado ──────────────────────────────────
      // El backend devuelve la consulta creada con `id` (ver _consultation_to_response / bson_id_to_str).
      const newConsultationId = savedConsultation?.id ?? savedConsultation?._id;
      if (newConsultationId) {
        if (isUsHost && reportToGenerate) {
          await openUsVisitPdf(newConsultationId, reportToGenerate);
        }
        const destUrl = modoAula
          ? `/consulta/${newConsultationId}?modo=aula`
          : `/consulta/${newConsultationId}`;
        navigate(destUrl, { replace: true });
      } else {
        // Caída de seguridad si la respuesta no incluye id (no debería ocurrir con la API actual).
        navigate(`/pacientes/${targetPatientId}`, { replace: true });
      }

    } catch (error) {
      // ── Convert backend validation errors to readable text ─────────────────
      const raw = error?.response?.data?.detail;
      let detail;
      if (Array.isArray(raw)) {
        detail = raw.map((e) => {
          const loc = (e.loc || []).filter((l) => l !== "body").join(".");
          return loc ? `${loc}: ${e.msg || e.message || ""}` : (e.msg || e.message || JSON.stringify(e));
        }).join(". ");
      } else if (raw && typeof raw === "object" && !(raw instanceof Error)) {
        detail = raw.message || raw.msg || JSON.stringify(raw);
      } else {
        detail = typeof raw === "string" ? raw : error?.message || (isUsHost ? "Unknown error while saving visit." : "Error desconocido al guardar la consulta.");
      }

      const status = error?.response?.status;
      if (status === 403) {
        toast.error(
          isUsHost
            ? "Visit limit reached. Upgrade your plan to continue."
            : "Límite de consultas alcanzado. Actualice su plan para continuar.",
          { duration: 8000 }
        );
      } else if (status === 401) {
        toast.error(isUsHost ? "Session expired. Please sign in again." : "Sesión expirada. Por favor inicie sesión nuevamente.", { duration: 6000 });
      } else if (status === 422 || status === 400) {
        toast.error(isUsHost ? `Clinical validation issue: ${detail}` : `Error de validación: ${detail}`, { duration: 8000 });
      } else {
        toast.error(isUsHost ? `Unable to finalize this visit: ${detail}` : `No se pudo guardar la consulta: ${detail}`, { duration: 6000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return null;
    const diff = Math.floor((new Date() - lastSaved) / 1000);
    if (diff < 60) return isUsHost ? "a few seconds ago" : "hace unos segundos";
    if (diff < 3600) return isUsHost ? `${Math.floor(diff / 60)} min ago` : `hace ${Math.floor(diff / 60)} min`;
    return isUsHost ? `${Math.floor(diff / 3600)} h ago` : `hace ${Math.floor(diff / 3600)} h`;
  };

  const goToStep = (step) => {
    if (step < 1 || step > WORKFLOW_STEPS.length) return;
    setCurrentStep(freezeLatamScaleCapture ? resolveLatamWorkflowStep(step) : step);
  };

  const groupScalesByCategory = useCallback((scales) => {
    const groups = {};
    (scales || []).forEach((scale) => {
      const rawCat = String(scale.category || "General");
      const primary = rawCat.split("/")[0].trim();
      if (!groups[primary]) groups[primary] = [];
      groups[primary].push(scale);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0], "es"));
  }, []);

  const allScalesGrouped = useMemo(
    () => groupScalesByCategory(AVAILABLE_SCALES),
    [groupScalesByCategory]
  );

  const estimatedMinutes = Math.max(3, Math.round((selectedScales.length || 1) * 2.5));
  const modeLabelMap = {
    rapida: "Evaluación rápida",
    estandar: "Evaluación estándar",
    completa: "Evaluación completa",
    personalizada: "Personalizada",
  };
  const currentUsGuidedBlock =
    US_GUIDED_BLOCKS.find((b) => b.steps.includes(currentStep))?.label || "Guided Visit";

  useAuroraTour(TOUR_CONFIG.consulta.key, TOUR_CONFIG.consulta.steps, {
    enabled: !loading && profileComplete && currentStep === 5 && isUsHost,
    delay: 1500,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!modoAula && !profileComplete) {
    return (
      <Card className="max-w-lg mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" /> {isUsHost ? "Incomplete Profile" : "Perfil Incompleto"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{isUsHost ? "Complete your clinician profile before creating visits." : "Complete su perfil médico antes de crear consultas."}</p>
          <Button asChild><Link to="/configuracion">{isUsHost ? "Complete Profile" : "Completar Perfil"}</Link></Button>
        </CardContent>
      </Card>
    );
  }

  // ============ STEP RENDERERS ============
  const renderStepContent = () => {
    switch (currentStep) {

      // ═══════════════════════════════════════════════════
      case 1: // DATOS GENERALES
      // ═══════════════════════════════════════════════════
        return (
          <div className="space-y-6">
            {isUsHost && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
                <p className="text-sm font-semibold text-sky-900">Visit Purpose</p>
                <p className="text-xs text-sky-700 mt-1">Select patient and visit type to initialize a guided U.S. geriatric workflow.</p>
              </div>
            )}
            {/* Patient selection */}
            {!patientId && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">{isUsHost ? "Select patient" : "Seleccionar paciente"}</Label>
                <Select value={formData.paciente_id} onValueChange={handlePatientSelect}>
                  <SelectTrigger data-testid="patient-select" className="h-11">
                    <SelectValue placeholder={isUsHost ? "Select patient" : "Seleccionar paciente"} />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombres} {p.apellidos} — {p.edad} {isUsHost ? "years" : "años"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedPatient && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-200">
                <div className="h-12 w-12 rounded-full bg-sky-600 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                  {selectedPatient.nombres?.[0]}{selectedPatient.apellidos?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{selectedPatient.nombres} {selectedPatient.apellidos}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600">
                    <span>{selectedPatient.edad} {isUsHost ? "years" : "años"}</span>
                    <span>·</span>
                    <span>{selectedPatient.genero || "N/A"}</span>
                    {selectedPatient.cuidador_principal && <><span>·</span><span>{isUsHost ? "Caregiver" : "Cuidador"}: {selectedPatient.cuidador_principal}</span></>}
                    {selectedPatient.telefono && <><span>·</span><span>{selectedPatient.telefono}</span></>}
                  </div>
                  {selectedPatient.seguro_medico && <p className="text-xs text-slate-500 mt-0.5">{isUsHost ? "Insurance" : "Seguro"}: {selectedPatient.seguro_medico}</p>}
                </div>
              </div>
            )}

            <Separator />

            <div className="space-y-3">
              <Label className="text-sm font-semibold">{isUsHost ? "Visit type" : "Tipo de consulta"}</Label>
              <div className="flex gap-3">
                {(isUsHost ? USA_VISIT_TYPES : CONSULTATION_TYPES).map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => (isUsHost ? applyUsVisitTemplate(t.value) : updateField("tipo_consulta", t.value))}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                      formData.tipo_consulta === t.value
                        ? "bg-sky-600 border-sky-600 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid={`consultation-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════════
      case 2: // MOTIVO DE CONSULTA
      // ═══════════════════════════════════════════════════
        return (
          <div className="space-y-5">
            {isUsHost && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <p className="text-sm font-semibold text-slate-900">Visit Purpose</p>
                <p className="text-xs text-slate-600 mt-1">Capture chief concern and concise interval history to guide the clinical review.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-semibold">{isUsHost ? "Chief Concern" : "Motivo principal de consulta"}</Label>
              <Textarea
                placeholder={isUsHost ? "Document the primary concern for this visit..." : "Describa el motivo principal..."}
                value={isUsHost ? formData.chief_concern : formData.motivo_consulta}
                onChange={e => updateField(isUsHost ? "chief_concern" : "motivo_consulta", e.target.value)}
                className="min-h-[90px] resize-none"
                data-testid="motivo-consulta"
              />
              {!isUsHost && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {MOTIVO_PHRASES.map(phrase => (
                    <button
                      key={phrase}
                      type="button"
                      onClick={() => updateField("motivo_consulta", formData.motivo_consulta ? `${formData.motivo_consulta}; ${phrase}` : phrase)}
                      className="text-xs px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-colors"
                    >
                      + {phrase}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUsHost ? "Duration / onset" : "Tiempo de evolución"}</Label>
                <Input placeholder={isUsHost ? "e.g., 3 days, 2 weeks, chronic..." : "Ej: 3 días, 2 semanas, crónico..."} value={formData.tiempo_evolucion} onChange={e => updateField("tiempo_evolucion", e.target.value)} data-testid="tiempo-evolucion" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">{isUsHost ? "History Summary" : "Historia de la enfermedad actual"}</Label>
              <Textarea
                placeholder={isUsHost ? "Summarize interval history, current status, and key context..." : "Descripción detallada del padecimiento actual: inicio, características, síntomas acompañantes, evolución..."}
                value={isUsHost ? formData.history_summary : formData.historia_enfermedad_actual}
                onChange={e => updateField(isUsHost ? "history_summary" : "historia_enfermedad_actual", e.target.value)}
                className="min-h-[120px] resize-none"
              />
            </div>

            {isUsHost && (
              <>
                <div className="space-y-3">
                  <Label className="font-semibold">4Ms Framework</Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>What Matters</Label>
                      <Textarea value={formData.four_ms_what_matters} onChange={(e) => updateField("four_ms_what_matters", e.target.value)} className="min-h-[90px] resize-none" />
                    </div>
                    <div className="space-y-2">
                      <Label>Medication</Label>
                      <Textarea value={formData.four_ms_medication} onChange={(e) => updateField("four_ms_medication", e.target.value)} className="min-h-[90px] resize-none" />
                    </div>
                    <div className="space-y-2">
                      <Label>Mentation</Label>
                      <Textarea value={formData.four_ms_mentation} onChange={(e) => updateField("four_ms_mentation", e.target.value)} className="min-h-[90px] resize-none" />
                    </div>
                    <div className="space-y-2">
                      <Label>Mobility</Label>
                      <Textarea value={formData.four_ms_mobility} onChange={(e) => updateField("four_ms_mobility", e.target.value)} className="min-h-[90px] resize-none" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">What Matters</Label>
                  <p className="text-xs text-slate-600">
                    Capture patient-defined goals and care preferences for practical follow-up planning.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>What matters most to you right now?</Label>
                      <Textarea
                        value={formData.what_matters_patient_priorities}
                        onChange={(e) => updateField("what_matters_patient_priorities", e.target.value)}
                        className="min-h-[90px] resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Main health goal</Label>
                      <Input
                        value={formData.what_matters_main_goal}
                        onChange={(e) => updateField("what_matters_main_goal", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Biggest current concern</Label>
                      <Input
                        value={formData.what_matters_biggest_concern}
                        onChange={(e) => updateField("what_matters_biggest_concern", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Care priorities discussed with patient/caregiver</Label>
                      <Textarea
                        value={formData.what_matters_shared_priorities}
                        onChange={(e) => updateField("what_matters_shared_priorities", e.target.value)}
                        className="min-h-[90px] resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Decision-maker / caregiver involved</Label>
                      <Input
                        value={formData.what_matters_decision_maker}
                        onChange={(e) => updateField("what_matters_decision_maker", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Advance directive status</Label>
                      <Input
                        placeholder="e.g., documented, discussed, not available"
                        value={formData.what_matters_advance_directive_status}
                        onChange={(e) => updateField("what_matters_advance_directive_status", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <p className="text-sm font-semibold text-slate-900">Clinical Screening</p>
                  <p className="text-xs text-slate-600 mt-1">Structured fall, cognition, mood, and functional screening for operational follow-up.</p>
                </div>

                {formData.tipo_consulta === "Annual Wellness Visit" && (
                  <div className="space-y-3">
                    <Label className="font-semibold">AWV Structured Sections</Label>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Health Risk Assessment</Label><Textarea value={formData.awv_health_risk_assessment} onChange={(e) => updateField("awv_health_risk_assessment", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2"><Label>Functional Status</Label><Textarea value={formData.awv_functional_status} onChange={(e) => updateField("awv_functional_status", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2"><Label>Fall Risk Screening</Label><Textarea value={formData.awv_fall_risk_screening} onChange={(e) => updateField("awv_fall_risk_screening", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2"><Label>Cognitive Screening</Label><Textarea value={formData.awv_cognitive_screening} onChange={(e) => updateField("awv_cognitive_screening", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2"><Label>Depression Screening</Label><Textarea value={formData.awv_depression_screening} onChange={(e) => updateField("awv_depression_screening", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2"><Label>Medication Review</Label><Textarea value={formData.awv_medication_review} onChange={(e) => updateField("awv_medication_review", e.target.value)} className="min-h-[80px] resize-none" /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Preventive Services Plan</Label><Textarea value={formData.awv_preventive_services_plan} onChange={(e) => updateField("awv_preventive_services_plan", e.target.value)} className="min-h-[90px] resize-none" /></div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Fall Risk Screening</Label>
                    <Badge variant="outline" className="capitalize">{calculateUsFallRisk({
                      recent_falls: formData.fall_recent_falls,
                      fear_of_falling: formData.fall_fear_of_falling,
                      gait_balance_concern: formData.fall_gait_balance_concern,
                      assistive_device_use: formData.fall_assistive_device_use,
                      home_safety_concern: formData.fall_home_safety_concern,
                      orthostatic_dizziness_concern: formData.fall_orthostatic_dizziness_concern,
                      medication_related_fall_concern: formData.fall_medication_related_concern,
                    })}</Badge>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_recent_falls} onCheckedChange={(v) => updateField("fall_recent_falls", !!v)} />Recent falls</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_fear_of_falling} onCheckedChange={(v) => updateField("fall_fear_of_falling", !!v)} />Fear of falling</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_gait_balance_concern} onCheckedChange={(v) => updateField("fall_gait_balance_concern", !!v)} />Gait or balance concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_assistive_device_use} onCheckedChange={(v) => updateField("fall_assistive_device_use", !!v)} />Assistive device use</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_home_safety_concern} onCheckedChange={(v) => updateField("fall_home_safety_concern", !!v)} />Home safety concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.fall_orthostatic_dizziness_concern} onCheckedChange={(v) => updateField("fall_orthostatic_dizziness_concern", !!v)} />Orthostatic or dizziness concern</label>
                    <label className="flex items-center gap-2 text-sm md:col-span-2"><Checkbox checked={formData.fall_medication_related_concern} onCheckedChange={(v) => updateField("fall_medication_related_concern", !!v)} />Medication-related fall concern</label>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Cognitive Screening</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.cognitive_memory_concern} onCheckedChange={(v) => updateField("cognitive_memory_concern", !!v)} />Memory concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.cognitive_orientation_concern} onCheckedChange={(v) => updateField("cognitive_orientation_concern", !!v)} />Orientation concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.cognitive_caregiver_concern} onCheckedChange={(v) => updateField("cognitive_caregiver_concern", !!v)} />Caregiver concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.cognitive_follow_up_recommended} onCheckedChange={(v) => updateField("cognitive_follow_up_recommended", !!v)} />Follow-up recommended</label>
                  </div>
                  <div className="space-y-2">
                    <Label>Brief clinical impression</Label>
                    <Textarea value={formData.cognitive_brief_impression} onChange={(e) => updateField("cognitive_brief_impression", e.target.value)} className="min-h-[90px] resize-none" />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Depression Screening</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.depression_depressed_mood_concern} onCheckedChange={(v) => updateField("depression_depressed_mood_concern", !!v)} />Depressed mood concern</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.depression_loss_of_interest_concern} onCheckedChange={(v) => updateField("depression_loss_of_interest_concern", !!v)} />Loss of interest concern</label>
                    <label className="flex items-center gap-2 text-sm md:col-span-2"><Checkbox checked={formData.depression_follow_up_recommended} onCheckedChange={(v) => updateField("depression_follow_up_recommended", !!v)} />Follow-up recommended</label>
                  </div>
                  <div className="space-y-2">
                    <Label>Brief impression</Label>
                    <Textarea value={formData.depression_brief_impression} onChange={(e) => updateField("depression_brief_impression", e.target.value)} className="min-h-[90px] resize-none" />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Functional Status</Label>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Mobility</Label>
                      <Select value={formData.functional_mobility_status} onValueChange={(value) => updateField("functional_mobility_status", value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="independent">Independent</SelectItem>
                          <SelectItem value="needs_help">Needs help</SelectItem>
                          <SelectItem value="dependent">Dependent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">ADLs</Label>
                      <Select value={formData.functional_adl_status} onValueChange={(value) => updateField("functional_adl_status", value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="independent">Independent</SelectItem>
                          <SelectItem value="needs_help">Needs help</SelectItem>
                          <SelectItem value="dependent">Dependent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">IADLs</Label>
                      <Select value={formData.functional_iadl_status} onValueChange={(value) => updateField("functional_iadl_status", value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="independent">Independent</SelectItem>
                          <SelectItem value="needs_help">Needs help</SelectItem>
                          <SelectItem value="dependent">Dependent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Functional summary</Label>
                    <Textarea value={formData.functional_summary} onChange={(e) => updateField("functional_summary", e.target.value)} className="min-h-[90px] resize-none" />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Social Risk Screening / SDOH</Label>
                  <p className="text-xs text-slate-600">
                    Screening-based social context to identify support needs and guide care planning follow-up.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.sdoh_food_insecurity} onCheckedChange={(v) => updateField("sdoh_food_insecurity", !!v)} />Food insecurity</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.sdoh_housing_instability} onCheckedChange={(v) => updateField("sdoh_housing_instability", !!v)} />Housing instability</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.sdoh_transportation_barriers} onCheckedChange={(v) => updateField("sdoh_transportation_barriers", !!v)} />Transportation barriers</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.sdoh_utility_difficulties} onCheckedChange={(v) => updateField("sdoh_utility_difficulties", !!v)} />Utility difficulties</label>
                    <label className="flex items-center gap-2 text-sm md:col-span-2"><Checkbox checked={formData.sdoh_safety_or_support_needs} onCheckedChange={(v) => updateField("sdoh_safety_or_support_needs", !!v)} />Safety or support needs</label>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Advance Care Planning</Label>
                  <p className="text-xs text-slate-600">
                    Documentation-focused ACP capture for proxy, directive status, and recommended follow-up.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_discussion_completed} onCheckedChange={(v) => updateField("acp_discussion_completed", !!v)} />ACP discussion documented</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_healthcare_proxy_identified} onCheckedChange={(v) => updateField("acp_healthcare_proxy_identified", !!v)} />Healthcare proxy identified</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_advance_directive_on_file} onCheckedChange={(v) => updateField("acp_advance_directive_on_file", !!v)} />Advance directive on file</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_goals_of_care_discussed} onCheckedChange={(v) => updateField("acp_goals_of_care_discussed", !!v)} />Goals of care discussed</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_code_status_discussed} onCheckedChange={(v) => updateField("acp_code_status_discussed", !!v)} />Code status discussed</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.acp_follow_up_needed} onCheckedChange={(v) => updateField("acp_follow_up_needed", !!v)} />ACP follow-up needed</label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Clinical Review & Care Plan</Label>
                  <Textarea value={formData.assessment_plan} onChange={(e) => updateField("assessment_plan", e.target.value)} className="min-h-[120px] resize-none" />
                </div>
              </>
            )}
          </div>
        );

      // ═══════════════════════════════════════════════════
      case 3: // ANTECEDENTES
      // ═══════════════════════════════════════════════════
        {
          const isFollowUpAnt = followUpContext && !followUpContext.is_first_consultation && (formData.tipo_consulta === "Control" || formData.tipo_consulta === "Reconsulta");
          const showReadOnly = isFollowUpAnt && !antecedentesEditMode;

          if (showReadOnly) {
            return (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Antecedentes de la historia clínica base. Edite solo si hay cambios.</p>
                  <Button variant="outline" size="sm" onClick={() => setAntecedentesEditMode(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Editar antecedentes
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Stethoscope className="h-3.5 w-3.5" /> Patológicos</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{formData.antecedentes_patologicos || "—"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Quirúrgicos</p>
                    <p className="text-sm text-slate-700">{formData.antecedentes_quirurgicos || "—"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> No patológicos</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{formData.antecedentes_no_patologicos || "—"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Familiares</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{formData.antecedentes_familiares || "—"}</p>
                  </div>
                  <div className="sm:col-span-2 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2">Alergias</p>
                    <p className="text-sm text-amber-900">{formData.alergias || "Ninguna registrada"}</p>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label className="font-semibold flex items-center gap-2">
                    <Pill className="h-4 w-4 text-sky-500" />
                    Medicamentos actuales del paciente
                  </Label>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-sm text-slate-700">{antMedications.filter(m => m.nombre).map(m => m.nombre).join(", ") || "Ninguno registrado"}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setAntecedentesEditMode(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Editar medicamentos
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div className="space-y-6">
              {isFollowUpAnt && (
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setAntecedentesEditMode(false)}>
                  ← Volver a vista de solo lectura
                </Button>
              )}
              <div className="space-y-2">
                <Label className="font-semibold flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-sky-500" />
                  Antecedentes patológicos
                </Label>
                <Textarea
                  placeholder="Enfermedades crónicas, cirugías previas, hospitalizaciones, comorbilidades..."
                  value={formData.antecedentes_patologicos}
                  onChange={e => updateField("antecedentes_patologicos", e.target.value)}
                  className="min-h-[80px] resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label>Antecedentes quirúrgicos</Label>
                <Input
                  placeholder="Cirugías previas y año..."
                  value={formData.antecedentes_quirurgicos}
                  onChange={e => updateField("antecedentes_quirurgicos", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-500" />
                  Antecedentes no patológicos
                </Label>
                <Textarea
                  placeholder="Alimentación, actividad física, tabaquismo, alcohol, hábitos, escolaridad, ocupación..."
                  value={formData.antecedentes_no_patologicos}
                  onChange={e => updateField("antecedentes_no_patologicos", e.target.value)}
                  className="min-h-[70px] resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-violet-500" />
                  Antecedentes familiares
                </Label>
                <Textarea
                  placeholder="Enfermedades hereditarias, condiciones en familiares directos..."
                  value={formData.antecedentes_familiares}
                  onChange={e => updateField("antecedentes_familiares", e.target.value)}
                  className="min-h-[60px] resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label>Alergias</Label>
                <Input
                  placeholder="Medicamentos, alimentos, látex..."
                  value={formData.alergias}
                  onChange={e => updateField("alergias", e.target.value)}
                />
              </div>
              <Separator />
              <div className="space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <Pill className="h-4 w-4 text-sky-500" />
                  Medicamentos actuales del paciente
                </Label>
                <p className="text-xs text-slate-500">Medicación habitual previa a esta consulta. Escriba el nombre y presione Enter.</p>
                <MedicationChipInput
                  medications={antMedications}
                  setMedications={setAntMedications}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              </div>
            </div>
          );
        }

      // ═══════════════════════════════════════════════════
      case 4: // EXAMEN FÍSICO
      // ═══════════════════════════════════════════════════
        return (
          <div className="space-y-6">
            {/* ── Signos vitales ── */}
            <div className="space-y-3">
              <Label className="font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5 text-sky-600" />
                Signos vitales
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {[
                  { field: "sbp",  label: "PA Sistólica",  placeholder: "mmHg" },
                  { field: "dbp",  label: "PA Diastólica", placeholder: "mmHg" },
                  { field: "hr",   label: "FC (lpm)",      placeholder: "lpm" },
                  { field: "rr",   label: "FR (rpm)",      placeholder: "rpm" },
                  { field: "temp", label: "Temp (°C)",     placeholder: "°C",  step: "0.1" },
                  { field: "spo2", label: "SpO2 (%)",      placeholder: "%" },
                  { field: "glucose", label: "Glucosa",    placeholder: "mg/dL" }
                ].map(({ field, label, placeholder, step }) => (
                  <div key={field} className="space-y-1">
                    <Label className="text-xs text-slate-500">{label}</Label>
                    <Input type="number" placeholder={placeholder} step={step} value={formData.vitals[field]}
                      onChange={e => updateVitals(field, e.target.value)} className="h-9" />
                  </div>
                ))}
              </div>

              {/* Blood pressure interpretation */}
              {formData.vitals.sbp && formData.vitals.dbp && (() => {
                const sbp = parseFloat(formData.vitals.sbp), dbp = parseFloat(formData.vitals.dbp);
                const [cls, label] = sbp >= 180 || dbp >= 110 ? ["bg-red-100 border-red-300 text-red-700", "HTA estadio 2"] :
                  sbp >= 140 || dbp >= 90 ? ["bg-amber-100 border-amber-300 text-amber-700", "HTA estadio 1"] :
                  sbp >= 130 || dbp >= 80 ? ["bg-amber-50 border-amber-200 text-amber-700", "HTA elevada"] :
                  ["bg-emerald-50 border-emerald-200 text-emerald-700", "Normal"];
                return (
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border ${cls}`}>
                    <span>PA: {formData.vitals.sbp}/{formData.vitals.dbp} mmHg</span>
                    <span className="opacity-70">— {label}</span>
                  </div>
                );
              })()}
            </div>

            <Separator />

            {/* ── Medidas antropométricas ── */}
            <div className="space-y-3">
              <Label className="font-semibold flex items-center gap-2">
                <Scale className="h-5 w-5 text-sky-600" />
                Medidas antropométricas
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {[
                  { field: "weight",     label: "Peso (kg)",              placeholder: "kg",   step: "0.1" },
                  { field: "height",     label: "Talla (cm)",             placeholder: "cm" },
                  { field: "waist_circ", label: "Circunf. abdominal (cm)",placeholder: "cm",   step: "0.1" },
                  { field: "calf_circ",  label: "Circunf. pantorrilla (cm)",placeholder: "cm", step: "0.1" },
                  { field: "arm_circ",   label: "Circunf. brazo (cm)",    placeholder: "cm",   step: "0.1" },
                  { field: "weight_loss",label: "Pérdida de peso reciente (kg)", placeholder: "kg ej: 3", step: "0.1" }
                ].map(({ field, label, placeholder, step }) => (
                  <div key={field} className="space-y-1">
                    <Label className="text-xs text-slate-500">{label}</Label>
                    <Input type="number" placeholder={placeholder} step={step || "1"}
                      value={formData.vitals[field]}
                      onChange={e => updateVitals(field, e.target.value)} className="h-9" />
                  </div>
                ))}
              </div>

              {/* Derived interpretations */}
              <div className="flex flex-wrap gap-2">
                {/* BMI chip — auto-calculated */}
                {formData.vitals.bmi && (() => {
                  const bmi = parseFloat(formData.vitals.bmi);
                  const [cls, label] = bmi < 18.5 ? ["bg-amber-50 border-amber-200 text-amber-700","Bajo peso"] :
                    bmi < 25 ? ["bg-emerald-50 border-emerald-200 text-emerald-700","Peso normal"] :
                    bmi < 30 ? ["bg-amber-50 border-amber-200 text-amber-700","Sobrepeso"] :
                    ["bg-red-50 border-red-200 text-red-700","Obesidad"];
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${cls}`}>
                      IMC: <strong>{formData.vitals.bmi}</strong> — {label}
                    </span>
                  );
                })()}

                {/* Abdominal circumference interpretation */}
                {formData.vitals.waist_circ && (() => {
                  const wc = parseFloat(formData.vitals.waist_circ);
                  // Use universal thresholds (M >102 / F >88 — approximation; chart shows both)
                  const risk = wc >= 102;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${risk ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                      C. abdominal: {wc} cm {risk ? "— Riesgo cardiovascular elevado" : ""}
                    </span>
                  );
                })()}

                {/* Calf circumference — surrogate for muscle mass in elderly */}
                {formData.vitals.calf_circ && (() => {
                  const cc = parseFloat(formData.vitals.calf_circ);
                  const low = cc < 31;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${low ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                      C. pantorrilla: {cc} cm {low ? "— Posible sarcopenia (<31 cm)" : "— Normal (≥31 cm)"}
                    </span>
                  );
                })()}

                {/* Recent weight loss alert */}
                {formData.vitals.weight_loss && parseFloat(formData.vitals.weight_loss) > 0 && (() => {
                  const wl = parseFloat(formData.vitals.weight_loss);
                  const significant = wl >= 4.5;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${significant ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                      Pérdida reciente: {wl} kg {significant ? "— Clínicamente significativa (≥4.5 kg)" : "— Monitoreada"}
                    </span>
                  );
                })()}
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Notas de signos vitales y antropometría</Label>
                <Textarea placeholder="Observaciones adicionales..." value={formData.vitals.notes}
                  onChange={e => updateVitals("notes", e.target.value)} className="min-h-[48px] resize-none" />
              </div>
            </div>

            <Separator />

            {/* Physical exam by systems */}
            <div className="space-y-3">
              <Label className="font-semibold flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-sky-600" />
                Exploración por sistemas
              </Label>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { field: "examen_aspecto_general", label: "Aspecto general", placeholder: "Estado general, conciencia, actitud, hidratación..." },
                  { field: "examen_cardiovascular", label: "Cardiovascular", placeholder: "Ruidos cardíacos, soplos, pulsos, edemas..." },
                  { field: "examen_respiratorio", label: "Respiratorio", placeholder: "Murmullo vesicular, estertores, sibilancias..." },
                  { field: "examen_neurologico", label: "Neurológico", placeholder: "Alerta, pares craneales, fuerza, sensibilidad, marcha..." },
                  { field: "examen_musculoesqueletico", label: "Musculoesquelético", placeholder: "Articulaciones, deformidades, movilidad..." },
                  { field: "examen_abdomen", label: "Abdomen", placeholder: "Ruidos intestinales, masas, hepatomegalia, dolor..." },
                  { field: "examen_piel", label: "Piel y tegumentos", placeholder: "Coloración, lesiones, úlceras, edema..." },
                  { field: "examen_otros", label: "Otros hallazgos", placeholder: "Cavidad oral, visión, audición, genitourinario..." }
                ].map(({ field, label, placeholder }) => (
                  <div key={field} className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">{label}</Label>
                    <Textarea
                      placeholder={placeholder}
                      value={formData[field]}
                      onChange={e => updateField(field, e.target.value)}
                      className="min-h-[60px] resize-none text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════════
      case 5: // ESCALAS GERIÁTRICAS
      // ═══════════════════════════════════════════════════
        if (freezeLatamScaleCapture) {
          return (
            <p className="text-sm text-slate-600" data-testid="consultation-scales-frozen">
              La aplicación digital de escalas no forma parte de las consultas nuevas. Use Continuar para seguir el flujo clínico.
            </p>
          );
        }
        return (
          <div className="space-y-7">
            {/* Main selector */}
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
              <Label className="font-semibold text-slate-800 flex items-center gap-2">
                <Target className="h-4 w-4 text-sky-600" />
                Ruta de evaluación
              </Label>
              <p className="text-xs text-slate-500">Seleccione una modalidad para activar escalas con menos pasos.</p>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { id: "rapida", title: "Evaluación rápida", subtitle: "Tamizaje útil en pocos minutos", count: QUICK_PACKAGE.length, recommended: false },
                  { id: "estandar", title: "Evaluación estándar", subtitle: "Recomendada por Aurora", count: STANDARD_PACKAGE.length, recommended: true },
                  { id: "completa", title: "Evaluación completa", subtitle: "Cobertura geriátrica amplia", count: COMPLETE_PACKAGE.length, recommended: false },
                  { id: "personalizada", title: "Personalizada", subtitle: "Selección manual por criterio clínico", count: selectedScales.length, recommended: false },
                ].map((mode) => {
                  const active = scaleSelectionMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => applyScaleSelectionMode(mode.id)}
                      className={`text-left p-3.5 rounded-xl border transition-all ${
                        active
                          ? mode.recommended
                            ? "bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200 shadow-sm"
                            : "bg-sky-50 border-sky-300 ring-1 ring-sky-200 shadow-sm"
                          : mode.recommended
                            ? "bg-emerald-50/40 border-emerald-200 hover:border-emerald-300"
                            : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800">{mode.title}</p>
                        <div className="flex items-center gap-1.5">
                          {mode.recommended && (
                            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Recomendada</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">{mode.count} escalas</Badge>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{mode.subtitle}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Summary indicators */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3.5">
                <p className="text-[11px] uppercase tracking-wide text-slate-600">Escalas activas</p>
                <p className="text-xl font-semibold text-slate-900 mt-0.5">{selectedScales.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <p className="text-[11px] uppercase tracking-wide text-slate-600">Tiempo estimado</p>
                <p className="text-xl font-semibold text-slate-900 mt-0.5">~{estimatedMinutes} min</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <p className="text-[11px] uppercase tracking-wide text-slate-600">Categoría principal</p>
                <p className="text-sm font-semibold text-slate-900 mt-1">{modeLabelMap[scaleSelectionMode] || scaleSelectionMode}</p>
              </div>
            </div>

            {/* Guías sugeridas — paquetes prearmados (siempre visibles) */}
            <div
              data-aurora-tour="consulta-guias"
              className="space-y-3 rounded-2xl border border-amber-200/80 bg-gradient-to-b from-amber-50/50 to-white p-4 md:p-5"
            >
              <div>
                <Label className="font-semibold text-slate-800 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  Guías sugeridas
                </Label>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                  Paquetes frecuentes según el foco de la visita. Después puede activar o quitar escalas individuales en la cuadrícula inferior.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {SCALE_PACKAGES.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => {
                      setSelectedScales(pkg.scales);
                      setScaleSelectionMode(inferScaleSelectionMode(pkg.scales));
                      setHasUnsavedChanges(true);
                    }}
                    className="text-xs px-3 py-2 rounded-xl bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100 transition-colors font-medium text-left max-w-[220px]"
                  >
                    <span className="block font-semibold">{pkg.name}</span>
                    <span className="block text-[10px] text-sky-600/90 font-normal mt-0.5">{pkg.scales.length} escalas</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedScales([]);
                    setScaleSelectionMode("personalizada");
                    setHasUnsavedChanges(true);
                  }}
                  className="text-xs px-3 py-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 transition-colors font-medium"
                >
                  Limpiar selección
                </button>
              </div>
            </div>

            {/* Todas las escalas — selección individual visible */}
            <div
              data-aurora-tour="consulta-escalas"
              className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50/40 p-4 md:p-5"
            >
              <div>
                <Label className="font-semibold text-slate-800 flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-sky-600" />
                  Escalas disponibles
                </Label>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                  Todas las escalas del catálogo. Pulse una tarjeta para incluirla o excluirla; las activas muestran un check y resaltado en color.
                </p>
              </div>
              {allScalesGrouped.map(([cat, catScales]) => (
                <div key={cat}>
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{cat}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[...catScales].sort((a, b) => a.name.localeCompare(b.name, "es")).map((scale) => {
                      const isSelected = selectedScales.includes(scale.id);
                      const prev = latestScales[scale.id];
                      const prevVal = prev?.puntaje ?? prev?.value;
                      const curr = calculateScoreFromAnswers(scale.id, scaleAnswers[scale.id]);
                      const hasBoth = prevVal != null && curr != null;
                      const worsened = isSelected && hasBoth && isScaleWorsened(scale.id, prevVal, curr);
                      const diff = hasBoth ? Number(curr) - Number(prevVal) : null;
                      return (
                        <button
                          type="button"
                          key={scale.id}
                          onClick={() => toggleScale(scale.id)}
                          className={`text-left flex gap-3 p-3 rounded-xl border transition-all ${
                            isSelected
                              ? worsened
                                ? "bg-red-50/90 border-red-300 ring-2 ring-red-100 shadow-sm"
                                : "bg-sky-50 border-sky-400 ring-2 ring-sky-200 shadow-sm"
                              : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/90"
                          }`}
                        >
                          <span className="shrink-0 mt-0.5" aria-hidden>
                            {isSelected ? (
                              <CheckCircle2 className="h-5 w-5 text-sky-600" />
                            ) : (
                              <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-slate-300 bg-white" />
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold leading-snug ${isSelected ? "text-slate-900" : "text-slate-800"}`}>{scale.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-snug">{scale.description}</p>
                            {isSelected && (prevVal != null || curr != null) && (
                              <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-slate-600">
                                {prevVal != null && <span>Ant: {String(prevVal)}</span>}
                                {curr != null && (
                                  <span className={worsened ? "font-medium text-red-700" : "font-medium text-slate-700"}>
                                    Act: {String(curr)}
                                    {diff != null && diff !== 0 && (
                                      <span
                                        className={`ml-1 ${
                                          worsened
                                            ? "text-red-600"
                                            : diff > 0 && !SCALE_HIGHER_WORSE.has(scale.id)
                                              ? "text-emerald-600"
                                              : "text-slate-500"
                                        }`}
                                      >
                                        ({diff > 0 ? "+" : ""}
                                        {diff})
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Interactive scale evaluation */}
            {selectedScales.length > 0 && (
              <>
                <Separator />
                <div>
                  <Label className="font-semibold flex items-center gap-2 mb-4">
                    <Scale className="h-4 w-4 text-sky-600" />
                    Aplicar escalas seleccionadas
                  </Label>
                  <ScalesEvaluationContainer
                    selectedScales={selectedScales}
                    scaleAnswers={scaleAnswers}
                    onScaleAnswersChange={(newAnswers) => { setScaleAnswers(newAnswers); setHasUnsavedChanges(true); }}
                  />
                </div>
                {/* Scale comparison: Previous / Current / Change */}
                {followUpContext && !followUpContext.is_first_consultation && (() => {
                  const comparisons = [];
                  selectedScales.forEach(scaleId => {
                    const prev = latestScales[scaleId]?.puntaje ?? latestScales[scaleId]?.value;
                    const curr = calculateScoreFromAnswers(scaleId, scaleAnswers[scaleId]);
                    if (prev != null && curr != null) {
                      const diff = Number(curr) - Number(prev);
                      const worsened = isScaleWorsened(scaleId, prev, curr);
                      const scaleInfo = AVAILABLE_SCALES.find(s => s.id === scaleId);
                      comparisons.push({ scaleId, scaleName: scaleInfo?.name || scaleId, prev, curr, diff, worsened });
                    }
                  });
                  if (comparisons.length === 0) return null;
                  return (
                    <div className="mt-4">
                      <Label className="font-semibold flex items-center gap-2 mb-3 text-slate-700">
                        <Activity className="h-4 w-4 text-sky-600" />
                        Comparación con evaluación previa
                      </Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {comparisons.map(({ scaleId, scaleName, prev, curr, diff, worsened }) => (
                          <div
                            key={scaleId}
                            className={`p-4 rounded-xl border ${worsened ? "bg-red-50/80 border-red-200" : diff === 0 ? "bg-slate-50 border-slate-200" : "bg-emerald-50/80 border-emerald-200"}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-800">{scaleName}</span>
                              {worsened && (
                                <span className="text-xs font-medium text-red-700 flex items-center gap-0.5">
                                  <TrendingDown className="h-3.5 w-3.5" />
                                  Deterioro
                                </span>
                              )}
                              {diff > 0 && !worsened && (
                                <span className="text-xs font-medium text-emerald-700 flex items-center gap-0.5">
                                  <TrendingUp className="h-3.5 w-3.5" />
                                  Mejoría
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div>
                                <p className="text-xs text-slate-500">Anterior</p>
                                <p className="font-semibold text-slate-700">{prev}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Actual</p>
                                <p className={`font-semibold ${worsened ? "text-red-700" : "text-slate-800"}`}>{curr}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Cambio</p>
                                <p className={`font-semibold ${worsened ? "text-red-700" : diff > 0 ? "text-emerald-700" : "text-slate-600"}`}>
                                  {diff > 0 ? `+${diff}` : diff}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            <Separator />

            {/* Social assessment */}
            <div className="space-y-2">
              <Label className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-violet-500" />
                Valoración social
              </Label>
              <Textarea
                placeholder="Red de apoyo, condiciones de vivienda, recursos económicos, situación familiar, cuidador..."
                value={formData.valoracion_social}
                onChange={e => updateField("valoracion_social", e.target.value)}
                className="min-h-[80px] resize-none"
              />
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════════
      case 6: // ESTUDIOS COMPLEMENTARIOS (opcional, colapsable)
      // ═══════════════════════════════════════════════════
        return (
          <div className="space-y-3">
            <p className="text-xs text-slate-600 leading-snug">
              {isUsHost
                ? "This section is optional — you may skip it."
                : "Este apartado es opcional — puedes omitirlo"}
            </p>
            <Collapsible defaultOpen={false} className="group">
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <div>
                  <p className="font-medium text-slate-700">
                    {isUsHost ? "Imaging & other studies" : "Imagen y otros estudios"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isUsHost ? "ECG, imaging, and other complementary studies (optional)" : "ECG, imagenología y otros estudios complementarios (opcional)"}
                  </p>
                </div>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  {((formData.estudios_relevantes || []).filter(e => e?.tipo_estudio || e?.resultado).length > 0)
                    ? "Ver / Editar"
                    : "Agregar"}
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="space-y-5 mt-5">
                <div className="space-y-2">
                  {((formData.estudios_relevantes || [])).map((est, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg">
                      <Input className="sm:col-span-3" placeholder={isUsHost ? "Type (ECG, X-ray, US...)" : "Tipo (ECG, Rx, USG...)"} value={est.tipo_estudio || ""}
                        onChange={e => updateField("estudios_relevantes", (formData.estudios_relevantes || []).map((x, i) => i === idx ? { ...x, tipo_estudio: e.target.value } : x))} />
                      <Input type="date" className="sm:col-span-2" value={est.fecha || ""}
                        onChange={e => updateField("estudios_relevantes", (formData.estudios_relevantes || []).map((x, i) => i === idx ? { ...x, fecha: e.target.value } : x))} />
                      <Input className="sm:col-span-3" placeholder="Resultado" value={est.resultado || ""}
                        onChange={e => updateField("estudios_relevantes", (formData.estudios_relevantes || []).map((x, i) => i === idx ? { ...x, resultado: e.target.value } : x))} />
                      <Input className="sm:col-span-3" placeholder="Comentario" value={est.comentario || ""}
                        onChange={e => updateField("estudios_relevantes", (formData.estudios_relevantes || []).map((x, i) => i === idx ? { ...x, comentario: e.target.value } : x))} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500"
                        onClick={() => updateField("estudios_relevantes", (formData.estudios_relevantes || []).filter((_, i) => i !== idx))}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => updateField("estudios_relevantes", [...(formData.estudios_relevantes || []), { tipo_estudio: "", fecha: "", resultado: "", comentario: "" }])}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar estudio
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          </div>
        );

      // ═══════════════════════════════════════════════════
      case 7: // DIAGNÓSTICOS
      // ═══════════════════════════════════════════════════
        {
          const scaleScores = {};
          selectedScales.forEach(id => {
            const score = calculateScoreFromAnswers(id, scaleAnswers[id]);
            if (score !== null) scaleScores[id] = score;
          });
          const syndromes = detectGeriatricSyndromes(scaleScores);
          const hasScores = Object.keys(scaleScores).length > 0;

          return (
            <div className="space-y-6">
              {/* Scale results summary */}
              {hasScores && (
                <div className="space-y-2">
                  <Label className="font-semibold">Resumen de escalas evaluadas</Label>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(scaleScores).map(([id, score]) => {
                      const interp = interpretScale(id, score);
                      const scale = AVAILABLE_SCALES.find(s => s.id === id);
                      const prevVal = latestScales[id]?.puntaje ?? latestScales[id]?.value;
                      const worsened = prevVal != null && isScaleWorsened(id, prevVal, score);
                      const isBinary = id === "cam" || id === "mini_cog";
                      const isHigh =
                        worsened ||
                        (id === "cam" && score === true) ||
                        (id === "mini_cog" && score === true) ||
                        (id === "polifarmacia_simple" && Number(score) >= 10) ||
                        interp?.toLowerCase().includes("delirium") ||
                        interp?.toLowerCase().includes("severo") ||
                        interp?.toLowerCase().includes("severa") ||
                        interp?.toLowerCase().includes("alto") ||
                        interp?.toLowerCase().includes("frágil");
                      const isMed =
                        !isHigh &&
                        (interp?.toLowerCase().includes("leve") ||
                          interp?.toLowerCase().includes("medio") ||
                          interp?.toLowerCase().includes("riesgo") ||
                          interp?.toLowerCase().includes("pre") ||
                          (id === "polifarmacia_simple" && Number(score) >= 5 && Number(score) < 10));
                      const diff = prevVal != null && !isBinary ? Number(score) - Number(prevVal) : null;
                      const displayMain =
                        isBinary && (score === true || score === false)
                          ? score
                            ? "Positivo"
                            : "Negativo"
                          : score;
                      const prevLabel =
                        prevVal === true || prevVal === 1
                          ? "Positivo"
                          : prevVal === false || prevVal === 0
                            ? "Negativo"
                            : prevVal;
                      return (
                        <div key={id} className={`p-3 rounded-xl border ${isHigh ? "bg-red-50 border-red-200" : isMed ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-slate-500">{scale?.name || id}</p>
                            {worsened && (
                              <span className="text-[10px] font-medium text-red-700 flex items-center gap-0.5">
                                <TrendingDown className="h-3 w-3" /> Deterioro
                              </span>
                            )}
                          </div>
                          <p className="text-2xl font-bold text-slate-800 mt-0.5">{displayMain}</p>
                          {prevVal != null && !isBinary && (
                            <p className="text-xs text-slate-500">
                              Anterior: {prevVal}
                              {diff != null && diff !== 0 && <span className={`ml-1 font-medium ${worsened ? "text-red-700" : "text-emerald-600"}`}>({diff > 0 ? "+" : ""}{diff})</span>}
                            </p>
                          )}
                          {prevVal != null && isBinary && (
                            <p className="text-xs text-slate-500">Anterior: {prevLabel}</p>
                          )}
                          <p className={`text-xs font-medium mt-0.5 ${isHigh ? "text-red-700" : isMed ? "text-amber-700" : "text-emerald-700"}`}>{interp}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Syndromes */}
              <div className="space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Síndromes geriátricos detectados
                </Label>
                {syndromes.length === 0 ? (
                  hasScores ? (
                    <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700">
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-medium">No se detectaron síndromes geriátricos de riesgo</span>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Complete las escalas en el paso anterior para detectar síndromes automáticamente.</p>
                  )
                ) : (
                  <div className="space-y-2">
                    {syndromes.map((s, i) => {
                      const high = ["fragilidad","riesgo_caidas","deterioro_cognitivo","dependencia_funcional"].includes(s.id);
                      return (
                        <div key={i} className={`p-4 rounded-xl border-l-4 ${high ? "bg-red-50 border-l-red-500" : "bg-amber-50 border-l-amber-500"}`}>
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold text-sm ${high ? "text-red-700" : "text-amber-800"}`}>{s.name}</p>
                            <div className="flex gap-1">
                              {s.triggeredBy.map(tb => <Badge key={tb} variant="outline" className={`text-[10px] ${high ? "border-red-300 text-red-600" : "border-amber-300 text-amber-600"}`}>{tb.toUpperCase()}</Badge>)}
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">{s.description}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Clinical impression */}
              <div className="space-y-2">
                <Label className="font-semibold">Impresión clínica</Label>
                <Textarea
                  placeholder="Estado geriátrico actual del paciente, hallazgos relevantes, interpretación clínica..."
                  value={formData.impresion_clinica}
                  onChange={e => updateField("impresion_clinica", e.target.value)}
                  className="min-h-[100px] resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas sobre síndromes detectados</Label>
                <Textarea
                  placeholder="Observaciones adicionales sobre los síndromes identificados..."
                  value={formData.sindrome_geriatrico}
                  onChange={e => updateField("sindrome_geriatrico", e.target.value)}
                  className="min-h-[60px] resize-none"
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Label className="font-semibold text-base">Diagnósticos clínicos</Label>
              <Button variant="outline" size="sm" onClick={addDiagnosis}>
                <Plus className="h-4 w-4 mr-1" /> Agregar
              </Button>
            </div>

            {formData.suggested_diagnoses?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs font-medium text-amber-700 mb-2">Sugerencias clínicas basadas en escalas:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.suggested_diagnoses.map((diag, i) => (
                    <button
                      key={i}
                      type="button"
                      className="text-xs px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 transition-colors"
                      onClick={() => setDiagnoses(d => [...d, { codigo: "", descripcion: diag, tipo: "secundario" }])}
                    >
                      + {diag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {diagnoses.map((diag, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-xl">
                  <div className="flex-1 grid sm:grid-cols-12 gap-2">
                    <div className="sm:col-span-2">
                      <Label className="text-[10px] text-slate-500 uppercase">Tipo</Label>
                      <Select value={diag.tipo} onValueChange={v => updateDiagnosis(i, "tipo", v)}>
                        <SelectTrigger className="h-8 text-xs mt-0.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="principal">Principal</SelectItem>
                          <SelectItem value="secundario">Secundario</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-3">
                      <Label className="text-[10px] text-slate-500 uppercase">CIE-10</Label>
                      <Input className="h-8 text-xs mt-0.5" placeholder="Código" value={diag.codigo} onChange={e => updateDiagnosis(i, "codigo", e.target.value)} />
                    </div>
                    <div className="sm:col-span-7">
                      <Label className="text-[10px] text-slate-500 uppercase">Descripción</Label>
                      <Input className="h-8 text-xs mt-0.5" placeholder="Diagnóstico clínico..." value={diag.descripcion} onChange={e => updateDiagnosis(i, "descripcion", e.target.value)} />
                    </div>
                  </div>
                  {diagnoses.length > 1 && (
                    <button type="button" onClick={() => removeDiagnosis(i)} className="mt-5 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            </div>
          );
        }

      // ═══════════════════════════════════════════════════
      case 8: // PLAN NO FARMACOLÓGICO
      // ═══════════════════════════════════════════════════
        {
          const scaleScores7 = {};
          selectedScales.forEach(id => {
            const score = calculateScoreFromAnswers(id, scaleAnswers[id]);
            if (score !== null) scaleScores7[id] = score;
          });

          return (
            <div className="space-y-5">
              {/* Non-pharmacological */}
              <div className="space-y-5">
                <Label className="font-semibold text-base flex items-center gap-2">
                  <Heart className="h-4 w-4 text-rose-500" />
                  Plan no farmacológico
                  <Badge variant="outline" className="text-xs ml-auto">Basado en guías internacionales</Badge>
                </Label>

                {/* Nutrition */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Apple className="h-4 w-4 text-green-500" />
                    <Label className="font-medium text-sm">Nutrición</Label>
                  </div>
                  <Textarea
                    placeholder="Plan dietético personalizado..."
                    value={formData.plan_dieta}
                    onChange={e => updateField("plan_dieta", e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>

                {/* Exercise */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Footprints className="h-4 w-4 text-blue-500" />
                    <Label className="font-medium text-sm">Ejercicio físico / Fisioterapia</Label>
                  </div>
                  <Textarea
                    placeholder="Plan de ejercicio y rehabilitación..."
                    value={formData.plan_ejercicio}
                    onChange={e => updateField("plan_ejercicio", e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>

                {/* Fall prevention */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-red-500" />
                    <Label className="font-medium text-sm">Prevención de caídas</Label>
                  </div>
                  <Textarea
                    placeholder="Medidas de prevención de caídas..."
                    value={formData.plan_prevencion_caidas}
                    onChange={e => updateField("plan_prevencion_caidas", e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>

                {/* Cognitive stimulation */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    <Label className="font-medium text-sm">Estimulación cognitiva / Terapia ocupacional</Label>
                  </div>
                  <Textarea
                    placeholder="Estimulación cognitiva y terapia ocupacional..."
                    value={formData.plan_estimulacion_cognitiva}
                    onChange={e => updateField("plan_estimulacion_cognitiva", e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>

                {/* Caregiver guidance */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-amber-500" />
                    <Label className="font-medium text-sm">Guía para cuidador / familia</Label>
                  </div>
                  <Textarea
                    placeholder="Instrucciones para el cuidador, signos de alarma..."
                    value={formData.plan_cuidador}
                    onChange={e => updateField("plan_cuidador", e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Otras indicaciones</Label>
                  <Textarea
                    placeholder="Interconsultas, estudios, otras recomendaciones..."
                    value={formData.plan_otros}
                    onChange={e => updateField("plan_otros", e.target.value)}
                    className="min-h-[60px] resize-none"
                  />
                </div>
              </div>
            </div>
          );
        }

      // ═══════════════════════════════════════════════════
      case 9: // TRATAMIENTO FARMACOLÓGICO
      // ═══════════════════════════════════════════════════
        {
          return (
            <div className="space-y-8">
              {/* Pharmacological */}
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Label className="font-semibold text-base flex items-center gap-2">
                      <Pill className="h-4 w-4 text-sky-600" />
                      Tratamiento farmacológico indicado
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">Medicamentos prescritos en esta consulta. Escriba el nombre y presione Enter.</p>
                  </div>
                  {followUpContext && !followUpContext.is_first_consultation && (formData.tipo_consulta === "Control" || formData.tipo_consulta === "Reconsulta") && followUpContext.last_consultation?.medicamentos_indicados?.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5 text-sky-600 border-sky-200 hover:bg-sky-50 hover:border-sky-300"
                      onClick={() => {
                        const prevMeds = followUpContext.last_consultation.medicamentos_indicados || [];
                        const existingNorm = new Set((medications || []).map(m => (m.nombre || "").trim().toLowerCase()).filter(Boolean));
                        const toAdd = prevMeds.filter(m => {
                          const n = (typeof m === "string" ? m : (m?.nombre || "")).toString().trim();
                          return n && !existingNorm.has(n.toLowerCase());
                        });
                        const newMeds = toAdd.map(m => {
                          if (typeof m === "string") return { ...emptyMedication, nombre: m };
                          return {
                            ...emptyMedication,
                            nombre: (m?.nombre || "").trim() || "",
                            dosis: m?.dosis || "",
                            frecuencia: m?.frecuencia || "",
                            duracion: m?.duracion || "",
                            indicacion: m?.indicacion || ""
                          };
                        });
                        if (newMeds.length > 0) {
                          setMedications(prev => [...prev, ...newMeds]);
                          setHasUnsavedChanges(true);
                          toast.success(`${newMeds.length} medicamento${newMeds.length !== 1 ? "s" : ""} añadidos desde consulta anterior`);
                        } else {
                          toast.info("Todos los medicamentos previos ya estaban agregados");
                        }
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Usar medicación de consulta anterior
                    </Button>
                  )}
                </div>
                <MedicationChipInput
                  medications={medications}
                  setMedications={setMedications}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Notas farmacológicas adicionales</Label>
                  <Textarea
                    placeholder="Ajustes de dosis, interacciones, consideraciones especiales..."
                    value={formData.plan_farmacologico_notas}
                    onChange={e => updateField("plan_farmacologico_notas", e.target.value)}
                    className="min-h-[60px] resize-none"
                  />
                </div>
              </div>
            </div>
          );
        }

      // ═══════════════════════════════════════════════════
      case 10: // CIERRE Y SEGUIMIENTO
      // ═══════════════════════════════════════════════════
        {
          const usaRiskLevel = calculateUsFallRisk({
            recent_falls: formData.fall_recent_falls,
            fear_of_falling: formData.fall_fear_of_falling,
            gait_balance_concern: formData.fall_gait_balance_concern,
            assistive_device_use: formData.fall_assistive_device_use,
            home_safety_concern: formData.fall_home_safety_concern,
            orthostatic_dizziness_concern: formData.fall_orthostatic_dizziness_concern,
            medication_related_fall_concern: formData.fall_medication_related_concern,
          });
          const medicationCount = (medications?.filter((m) => String(m?.nombre || "").trim()).length || 0)
            || (antMedications?.filter((m) => String(m?.nombre || "").trim()).length || 0);
          const usaSmartAlerts = buildUsSmartAlerts(formData, medicationCount);
          const usaKeyFindings = [
            formData.cognitive_memory_concern || formData.cognitive_orientation_concern ? "Cognitive screening concern present." : "",
            formData.depression_depressed_mood_concern || formData.depression_loss_of_interest_concern ? "Mood screening concern present." : "",
            formData.functional_mobility_status !== "independent" || formData.functional_adl_status !== "independent" || formData.functional_iadl_status !== "independent"
              ? "Functional support needs identified."
              : "",
            formData.awv_preventive_services_plan ? "Preventive services plan documented." : "",
          ].filter(Boolean);
          const displaySteadi = usSteadiPreview;
          const displayCognition = usCognitionPreview;
          const displayMood = usMoodPreview;
          const displayFunctional = usFunctionalPreview;
          const displayFrailty = usFrailtyPreview;
          const awvCompliance = calculateUsAwvCompliance(formData);
          const displayFallLevel = String(displaySteadi?.risk_level || usaRiskLevel || "").toLowerCase();
          const steadiStageLabel = displaySteadi?.steadi_stage
            ? String(displaySteadi.steadi_stage).replace(/_/g, " ")
            : "";
          const steadiPriorityLabel = displaySteadi?.priority
            ? String(displaySteadi.priority).replace(/_/g, " ")
            : "";
          const steadiPrimaryAction =
            Array.isArray(displaySteadi?.recommended_actions) && displaySteadi.recommended_actions.length
              ? displaySteadi.recommended_actions[0]
              : null;
          const cognitionStatusLabel = displayCognition?.cognitive_status
            ? String(displayCognition.cognitive_status).replace(/_/g, " ")
            : "";
          const cognitionPriorityLabel = displayCognition?.priority
            ? String(displayCognition.priority).replace(/_/g, " ")
            : "";
          const moodStatusLabel = displayMood?.mood_status
            ? String(displayMood.mood_status).replace(/_/g, " ")
            : "";
          const moodPriorityLabel = displayMood?.priority
            ? String(displayMood.priority).replace(/_/g, " ")
            : "";
          const functionalStatusLabel = displayFunctional?.functional_status
            ? String(displayFunctional.functional_status).replace(/_/g, " ")
            : "";
          const functionalPriorityLabel = displayFunctional?.priority
            ? String(displayFunctional.priority).replace(/_/g, " ")
            : "";
          const frailtyStatusLabel = displayFrailty?.frailty_status
            ? String(displayFrailty.frailty_status).replace(/_/g, " ")
            : "";
          const frailtyPriorityLabel = displayFrailty?.priority
            ? String(displayFrailty.priority).replace(/_/g, " ")
            : "";
          const medicationReview = calculateUsMedicationReviewStatus({
            medicationCount,
            fallRiskLevel: displayFallLevel,
            cognitionStatus: cognitionStatusLabel,
            frailtyStatus: frailtyStatusLabel,
          });
          const sdohReview = calculateUsSdohStatus(formData);
          const acpReview = calculateUsAcpStatus(formData);
          const sdohLabelMap = {
            food_insecurity: "Food insecurity",
            housing_instability: "Housing instability",
            transportation_barriers: "Transportation barriers",
            utility_difficulties: "Utility difficulties",
            safety_or_support_needs: "Safety/support needs",
          };

        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800">Consulta lista para finalizar</p>
                <p className="text-sm text-emerald-600 mt-0.5">Defina la próxima cita y luego guarde la consulta.</p>
              </div>
            </div>

            {/* Next visit quick options */}
            <div className="space-y-3">
              <Label className="font-semibold">Próxima cita</Label>
              <div className="flex flex-wrap gap-2">
                {NEXT_VISIT_OPTIONS.map(opt => {
                  const d = new Date();
                  d.setDate(d.getDate() + opt.days);
                  const dateStr = d.toISOString().slice(0, 10);
                  const selected = formData.proxima_cita === dateStr;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateField("proxima_cita", dateStr)}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${selected ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Fecha personalizada</Label>
                  <Input type="date" value={formData.proxima_cita} onChange={e => updateField("proxima_cita", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Hora</Label>
                  <Input type="time" value={formData.proxima_cita_hora} onChange={e => updateField("proxima_cita_hora", e.target.value)} className="w-32" />
                </div>
              </div>
            </div>

            {/* Consultation summary */}
            {!isUsHost && (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-sm">
                <p className="font-semibold text-slate-700 mb-2">Resumen de la consulta</p>
                <div className="grid grid-cols-2 gap-y-1 text-slate-600">
                  <span>Tipo:</span><span className="font-medium">{formData.tipo_consulta || "—"}</span>
                  <span>{isUsHost ? "Chief concern:" : "Motivo:"}</span><span className="font-medium truncate">{(formData.chief_concern || formData.motivo_consulta)?.substring(0, 40) || "—"}</span>
                  <span>Escalas:</span><span className="font-medium">{selectedScales.length} aplicadas</span>
                  <span>Medicamentos:</span><span className="font-medium">{medications.length} indicados</span>
                  <span>Diagnósticos:</span><span className="font-medium">{diagnoses.filter(d => d.descripcion.trim()).length} registrados</span>
                  <span>Próxima cita:</span><span className="font-medium">{formData.proxima_cita || "No definida"}</span>
                </div>
              </div>
            )}

            {isUsHost && (
              <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
                <div>
                  <p className="text-base font-semibold text-slate-900">Visit Review</p>
                  <p className="text-xs text-slate-500 mt-0.5">Clinical Findings Preview</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Visit Type</p>
                    <p className="font-medium text-slate-800 mt-1">{formData.tipo_consulta || "Not selected"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Chief Concern</p>
                    <p className="font-medium text-slate-800 mt-1">{(formData.chief_concern || formData.motivo_consulta) || "Not documented"}</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">4Ms Snapshot</p>
                  <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-700">
                    <p><span className="font-medium">What Matters:</span> {formData.four_ms_what_matters || "Not documented"}</p>
                    <p><span className="font-medium">Medication:</span> {formData.four_ms_medication || "Not documented"}</p>
                    <p><span className="font-medium">Mentation:</span> {formData.four_ms_mentation || "Not documented"}</p>
                    <p><span className="font-medium">Mobility:</span> {formData.four_ms_mobility || "Not documented"}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Medication Review / Polypharmacy</p>
                    <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-700">
                      <p><span className="font-medium">Medication count:</span> {medicationReview.medication_count}</p>
                      <p>
                        <span className="font-medium">Polypharmacy status:</span>{" "}
                        {medicationReview.polypharmacy_detected ? "Polypharmacy flagged (screening-based)" : "No polypharmacy flag"}
                      </p>
                      <p><span className="font-medium">Priority:</span> {String(medicationReview.priority || "routine").replace(/_/g, " ")}</p>
                      <p><span className="font-medium">Suggested follow-up:</span> {medicationReview.follow_up_interval}</p>
                      <p className="md:col-span-2"><span className="font-medium">Clinical reason:</span> {medicationReview.clinical_reason}</p>
                    </div>
                    {Array.isArray(medicationReview.recommended_actions) && medicationReview.recommended_actions.length ? (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Recommended actions</p>
                        <ul className="space-y-1">
                          {medicationReview.recommended_actions.map((a) => (
                            <li key={`med-action-${a}`} className="text-xs text-slate-700">- {a}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Social Risk Screening / SDOH</p>
                    <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-700">
                      <p><span className="font-medium">Status:</span> {String(sdohReview.sdoh_status || "no_positive_screen").replace(/_/g, " ")}</p>
                      <p><span className="font-medium">Priority:</span> {String(sdohReview.priority || "routine").replace(/_/g, " ")}</p>
                      <p><span className="font-medium">Suggested follow-up:</span> {sdohReview.follow_up_interval || "12 months"}</p>
                      <p><span className="font-medium">Positive domains:</span> {(sdohReview.positive_domains || []).map((d) => sdohLabelMap[d] || d).join(", ") || "None"}</p>
                      <p className="md:col-span-2"><span className="font-medium">Clinical reason:</span> {sdohReview.clinical_reason}</p>
                    </div>
                    {Array.isArray(sdohReview.recommended_actions) && sdohReview.recommended_actions.length ? (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Recommended actions</p>
                        <ul className="space-y-1">
                          {sdohReview.recommended_actions.map((a) => (
                            <li key={`sdoh-action-${a}`} className="text-xs text-slate-700">- {a}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Advance Care Planning</p>
                    <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-700">
                      <p><span className="font-medium">Status:</span> {String(acpReview.acp_status || "pending_documentation").replace(/_/g, " ")}</p>
                      <p><span className="font-medium">Priority:</span> {String(acpReview.priority || "routine").replace(/_/g, " ")}</p>
                      <p><span className="font-medium">Recommended follow-up:</span> {acpReview.follow_up_interval || "next preventive visit"}</p>
                      <p><span className="font-medium">Proxy identified:</span> {formData.acp_healthcare_proxy_identified ? "Yes" : "No"}</p>
                      <p className="md:col-span-2"><span className="font-medium">Clinical reason:</span> {acpReview.clinical_reason}</p>
                    </div>
                    {Array.isArray(acpReview.recommended_actions) && acpReview.recommended_actions.length ? (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Recommended actions</p>
                        <ul className="space-y-1">
                          {acpReview.recommended_actions.map((a) => (
                            <li key={`acp-action-${a}`} className="text-xs text-slate-700">- {a}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500">AWV Completion Status</p>
                    <p className="text-lg font-semibold text-slate-900 mt-1">{awvCompliance.awv_completion_score}% complete</p>
                    <div className="grid md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-1">Completed items</p>
                        {!awvCompliance.completed_items?.length ? (
                          <p className="text-xs text-slate-500">No documented items yet.</p>
                        ) : (
                          <ul className="space-y-1">
                            {awvCompliance.completed_items.map((item) => (
                              <li key={`awv-done-${item}`} className="text-xs text-slate-700">- {item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-1">Missing items</p>
                        {!awvCompliance.missing_items?.length ? (
                          <p className="text-xs text-emerald-700">All tracked items documented.</p>
                        ) : (
                          <ul className="space-y-1">
                            {awvCompliance.missing_items.map((item) => (
                              <li key={`awv-miss-${item}`} className="text-xs text-amber-700">- {item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500">What Matters</p>
                    <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-700">
                      <p><span className="font-medium">Patient priorities:</span> {formData.what_matters_patient_priorities || "Not documented"}</p>
                      <p><span className="font-medium">Main goal:</span> {formData.what_matters_main_goal || "Not documented"}</p>
                      <p><span className="font-medium">Biggest concern:</span> {formData.what_matters_biggest_concern || "Not documented"}</p>
                      <p><span className="font-medium">Decision-maker:</span> {formData.what_matters_decision_maker || "Not documented"}</p>
                      <p className="md:col-span-2"><span className="font-medium">Shared priorities:</span> {formData.what_matters_shared_priorities || "Not documented"}</p>
                      <p className="md:col-span-2"><span className="font-medium">Advance directive status:</span> {formData.what_matters_advance_directive_status || "Not documented"}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Cognitive Status (quick)</p>
                    <p className="font-medium text-slate-800 mt-1 capitalize">
                      {cognitionStatusLabel ||
                        (formData.cognitive_memory_concern || formData.cognitive_orientation_concern || formData.cognitive_caregiver_concern
                          ? "Screening inputs present — see structured care plan below"
                          : "No screening concerns toggled")}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Functional Status (quick)</p>
                    <p className="font-medium text-slate-800 mt-1">
                      {functionalStatusLabel
                        ? functionalStatusLabel
                        : formData.functional_mobility_status === "independent" && formData.functional_adl_status === "independent" && formData.functional_iadl_status === "independent"
                          ? "Independent"
                          : "Support may be needed — see structured care plan below"}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">STEADI care plan</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Structured fall-risk decisions</p>
                    </div>
                    {usSteadiPreviewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating…
                      </div>
                    )}
                  </div>

                  {usSteadiPreviewError && (
                    <p className="text-xs text-amber-700">{usSteadiPreviewError}</p>
                  )}

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Fall risk level</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{displayFallLevel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">STEADI stage</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{steadiStageLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Priority</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{steadiPriorityLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Suggested follow-up</p>
                      <p className="font-medium text-slate-900 mt-1">{displaySteadi?.follow_up_interval || "—"}</p>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Recommended actions</p>
                    {!displaySteadi?.recommended_actions?.length ? (
                      <p className="text-xs text-slate-500 mt-2">No structured actions yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {displaySteadi.recommended_actions.map((a, idx) => (
                          <li key={`${a?.type || "action"}-${idx}`} className="text-xs text-slate-700">
                            <span className="font-semibold text-slate-900">[{String(a?.type || "action")}]</span>{" "}
                            <span>{a?.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {steadiPrimaryAction?.text && (
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold text-slate-800">Primary focus:</span> {steadiPrimaryAction.text}
                    </p>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">Cognitive care plan</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Cognitive Care Plan (Screening-Based)</p>
                      <p className="text-xs text-slate-500 mt-1">
                        For follow-up planning only; does not establish a diagnosis. Content reflects structured screening inputs and
                        suggested—not definitive—next steps.
                      </p>
                    </div>
                    {usCognitionPreviewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating…
                      </div>
                    )}
                  </div>

                  {usCognitionPreviewError && <p className="text-xs text-amber-700">{usCognitionPreviewError}</p>}

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{cognitionStatusLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Priority</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{cognitionPriorityLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Clinical rationale</p>
                      <p className="text-sm text-slate-800 mt-1 leading-snug">{displayCognition?.clinical_reason || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Suggested follow-up interval</p>
                      <p className="font-medium text-slate-900 mt-1">{displayCognition?.follow_up_interval || "—"}</p>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Recommended actions</p>
                    {!displayCognition?.recommended_actions?.length ? (
                      <p className="text-xs text-slate-500 mt-2">No structured actions yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {displayCognition.recommended_actions.map((a, idx) => (
                          <li key={`cog-${a?.type || "action"}-${idx}`} className="text-xs text-slate-700">
                            <span className="font-semibold text-slate-900">[{String(a?.type || "action")}]</span>{" "}
                            <span>{a?.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">Mood care plan</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Structured mood follow-up (screening-based)</p>
                      <p className="text-xs text-slate-500 mt-1">
                        For follow-up planning only; does not establish a diagnosis. Wording below reflects screening inputs and
                        suggested—not definitive—next steps.
                      </p>
                    </div>
                    {usMoodPreviewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating…
                      </div>
                    )}
                  </div>

                  {usMoodPreviewError && <p className="text-xs text-amber-700">{usMoodPreviewError}</p>}

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Mood status</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{moodStatusLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Priority</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{moodPriorityLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Clinical rationale</p>
                      <p className="text-sm text-slate-800 mt-1 leading-snug">{displayMood?.clinical_reason || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Suggested follow-up interval</p>
                      <p className="font-medium text-slate-900 mt-1">{displayMood?.follow_up_interval || "—"}</p>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Recommended actions</p>
                    {!displayMood?.recommended_actions?.length ? (
                      <p className="text-xs text-slate-500 mt-2">No structured actions yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {displayMood.recommended_actions.map((a, idx) => (
                          <li key={`mood-${a?.type || "action"}-${idx}`} className="text-xs text-slate-700">
                            <span className="font-semibold text-slate-900">[{String(a?.type || "action")}]</span>{" "}
                            <span>{a?.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">Functional care plan</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Functional Support Care Plan (Screening-Based)</p>
                      <p className="text-xs text-slate-500 mt-1">
                        For follow-up and support planning only; does not establish a diagnosis. Wording reflects structured screening inputs and
                        suggested—not definitive—next steps.
                      </p>
                    </div>
                    {usFunctionalPreviewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating…
                      </div>
                    )}
                  </div>

                  {usFunctionalPreviewError && <p className="text-xs text-amber-700">{usFunctionalPreviewError}</p>}

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Functional status</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{functionalStatusLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Priority</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{functionalPriorityLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Clinical rationale</p>
                      <p className="text-sm text-slate-800 mt-1 leading-snug">{displayFunctional?.clinical_reason || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Suggested follow-up interval</p>
                      <p className="font-medium text-slate-900 mt-1">{displayFunctional?.follow_up_interval || "—"}</p>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Recommended actions</p>
                    {!displayFunctional?.recommended_actions?.length ? (
                      <p className="text-xs text-slate-500 mt-2">No structured actions yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {displayFunctional.recommended_actions.map((a, idx) => (
                          <li key={`fn-${a?.type || "action"}-${idx}`} className="text-xs text-slate-700">
                            <span className="font-semibold text-slate-900">[{String(a?.type || "action")}]</span>{" "}
                            <span>{a?.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-orange-100 bg-orange-50/50 p-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">FRAIL care plan</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Frailty Care Plan (Screening-Based)</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Screening-based guidance for follow-up planning only; does not establish a diagnosis. Interpret FRAIL alongside clinical context.
                      </p>
                    </div>
                    {usFrailtyPreviewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating…
                      </div>
                    )}
                  </div>

                  {usFrailtyPreviewError && <p className="text-xs text-amber-700">{usFrailtyPreviewError}</p>}

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Frailty status</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{frailtyStatusLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Priority</p>
                      <p className="font-medium text-slate-900 mt-1 capitalize">{frailtyPriorityLabel || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Clinical rationale</p>
                      <p className="text-sm text-slate-800 mt-1 leading-snug">{displayFrailty?.clinical_reason || "—"}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Suggested follow-up interval</p>
                      <p className="font-medium text-slate-900 mt-1">{displayFrailty?.follow_up_interval || "—"}</p>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Recommended actions</p>
                    {!displayFrailty?.recommended_actions?.length ? (
                      <p className="text-xs text-slate-500 mt-2">No structured actions yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {displayFrailty.recommended_actions.map((a, idx) => (
                          <li key={`fr-${a?.type || "action"}-${idx}`} className="text-xs text-slate-700">
                            <span className="font-semibold text-slate-900">[{String(a?.type || "action")}]</span>{" "}
                            <span>{a?.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isUsHost && (
              <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl space-y-3 text-sm">
                <p className="font-semibold text-sky-900">Auto Clinical Summary</p>
                <div className="grid gap-2 text-slate-700">
                  <p><span className="font-medium">Key findings:</span> {usaKeyFindings.length ? usaKeyFindings.join(" ") : "No major findings captured."}</p>
                  <p><span className="font-medium">Fall risk level:</span> <span className="capitalize">{displayFallLevel || usaRiskLevel}</span></p>
                  <p><span className="font-medium">STEADI stage:</span> <span className="capitalize">{steadiStageLabel || "—"}</span> · <span className="font-medium">Priority:</span> <span className="capitalize">{steadiPriorityLabel || "—"}</span></p>
                  <p><span className="font-medium">Suggested STEADI follow-up:</span> {displaySteadi?.follow_up_interval || "—"}</p>
                  <p>
                    <span className="font-medium">Cognition (screening):</span>{" "}
                    <span className="capitalize">{cognitionStatusLabel || "—"}</span>
                    {displayCognition?.priority ? (
                      <>
                        {" "}
                        · <span className="font-medium">Priority:</span>{" "}
                        <span className="capitalize">{cognitionPriorityLabel}</span>
                      </>
                    ) : null}
                    {displayCognition?.follow_up_interval ? (
                      <>
                        {" "}
                        · <span className="font-medium">Suggested cognitive follow-up:</span> {displayCognition.follow_up_interval}
                      </>
                    ) : null}
                  </p>
                  <p>
                    <span className="font-medium">Mood (screening):</span>{" "}
                    <span className="capitalize">{moodStatusLabel || "—"}</span>
                    {displayMood?.priority ? (
                      <>
                        {" "}
                        · <span className="font-medium">Priority:</span>{" "}
                        <span className="capitalize">{moodPriorityLabel}</span>
                      </>
                    ) : null}
                    {displayMood?.follow_up_interval ? (
                      <>
                        {" "}
                        · <span className="font-medium">Suggested mood follow-up:</span> {displayMood.follow_up_interval}
                      </>
                    ) : null}
                  </p>
                  <p>
                    <span className="font-medium">Functional (screening):</span>{" "}
                    <span className="capitalize">{functionalStatusLabel || "—"}</span>
                    {displayFunctional?.priority ? (
                      <>
                        {" "}
                        · <span className="font-medium">Priority:</span>{" "}
                        <span className="capitalize">{functionalPriorityLabel}</span>
                      </>
                    ) : null}
                    {displayFunctional?.follow_up_interval ? (
                      <>
                        {" "}
                        · <span className="font-medium">Suggested functional follow-up:</span> {displayFunctional.follow_up_interval}
                      </>
                    ) : null}
                  </p>
                  <p>
                    <span className="font-medium">Frailty (FRAIL screening):</span>{" "}
                    <span className="capitalize">{frailtyStatusLabel || "—"}</span>
                    {displayFrailty?.priority ? (
                      <>
                        {" "}
                        · <span className="font-medium">Priority:</span>{" "}
                        <span className="capitalize">{frailtyPriorityLabel}</span>
                      </>
                    ) : null}
                    {displayFrailty?.follow_up_interval ? (
                      <>
                        {" "}
                        · <span className="font-medium">Suggested frailty follow-up:</span> {displayFrailty.follow_up_interval}
                      </>
                    ) : null}
                  </p>
                  <p><span className="font-medium">AWV type:</span> {formData.awv_type || "Not specified"}</p>
                  <p><span className="font-medium">Recommendations:</span> {usaSmartAlerts.length ? usaSmartAlerts.join(" ") : "Continue routine monitoring and AWV preventive plan."}</p>
                  <p><span className="font-medium">Follow-up cadence:</span> {formData.proxima_cita ? `${formData.proxima_cita}${formData.proxima_cita_hora ? ` at ${formData.proxima_cita_hora}` : ""}` : "To be scheduled based on clinical priority."}</p>
                </div>
              </div>
            )}

            {isUsHost && buildUsSmartAlerts(formData).length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                <p className="font-semibold text-amber-900 text-sm">Smart Alerts</p>
                <ul className="space-y-1 text-sm text-amber-800">
                  {buildUsSmartAlerts(formData).map((alert) => (
                    <li key={alert}>- {alert}</li>
                  ))}
                </ul>
              </div>
            )}

            {isUsHost && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="font-semibold text-slate-800 text-sm">Document Actions</p>
                <div className="grid md:grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saving || !formData.paciente_id}
                    onClick={() => handleSubmit("clinical_summary")}
                  >
                    Generate Clinical Summary
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saving || !formData.paciente_id}
                    onClick={() => handleSubmit("prevention_plan")}
                  >
                    Generate Prevention Care Plan
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saving || !formData.paciente_id}
                    onClick={() => handleSubmit("caregiver_summary")}
                  >
                    Generate Patient & Caregiver Summary
                  </Button>
                </div>
              </div>
            )}

            <Button
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold disabled:opacity-70"
              onClick={handleSubmit}
              disabled={saving || !formData.paciente_id || isDemoUser}
              data-testid="finalize-consultation-btn"
            >
              {saving
                ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{isUsHost ? "Saving visit..." : modoAula ? "Guardando práctica..." : "Guardando consulta..."}</>
                : <><Check className="mr-2 h-5 w-5" />{isUsHost ? "Finalize and Save Visit" : modoAula ? "Guardar práctica" : "Finalizar y Guardar Consulta"}</>}
            </Button>
            {!formData.paciente_id && (
              <p className="text-xs text-center text-amber-600 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {isUsHost ? "Select a patient in Step 1 before saving." : "Seleccione un paciente en el Paso 1 para poder guardar."}
              </p>
            )}
            <p className="text-xs text-center text-slate-400">
              {isUsHost ? "Documents are generated directly from this visit." : "Los documentos se generan automáticamente desde la consulta."}
            </p>
          </div>
        );
        }

      default:
        return null;
    }
  };

  // ============ RENDER ============
  return (
    <div className="min-h-screen bg-slate-50">
      {modoAula && (
        <div className="bg-violet-50 border-b border-violet-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm text-violet-900 no-print">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 shrink-0" />
            <span>Aurora Aula — Entorno de práctica</span>
          </div>
          <Link to="/aula/dashboard" className="font-medium text-violet-700 hover:text-violet-900 shrink-0">
            ← Volver a Aula
          </Link>
        </div>
      )}
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm no-print">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-base">{isUsHost ? "New Visit" : "Nueva Consulta"}</h1>
                {isUsHost ? (
                  <p className="text-xs text-slate-500">Guided geriatric visit workflow for U.S. clinical practice</p>
                ) : null}
                {selectedPatient && (
                  <p className="text-sm text-slate-500">
                    {selectedPatient.nombres} {selectedPatient.apellidos} · {selectedPatient.edad} {isUsHost ? "years" : "años"}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {lastSaved && (
                <span className="text-xs text-slate-400 hidden sm:flex items-center gap-1">
                  <Clock className="h-3 w-3" />{isUsHost ? "Saved" : "Guardado"} {formatLastSaved()}
                </span>
              )}
              {hasUnsavedChanges && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">{isUsHost ? "Unsaved" : "Sin guardar"}</Badge>}
              <Button variant="outline" size="sm" onClick={() => handleSaveDraft(true)} disabled={saving || !formData.paciente_id}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {isUsHost ? "Save Draft" : "Guardar"}
              </Button>
            </div>
          </div>
        </div>

        {/* Barra de progreso (fija con el encabezado) */}
        {(() => {
          const meta = WORKFLOW_STEPS.find((s) => s.id === currentStep);
          const total = WORKFLOW_STEPS.length;
          const pct = Math.min(100, Math.round((currentStep / total) * 100));
          const atEnd = currentStep >= total;
          const stepTitle = isUsHost ? currentUsGuidedBlock : meta?.label;
          return (
            <div className="border-t border-slate-100 bg-white" data-aurora-tour="consulta-progress">
              <div className="max-w-5xl mx-auto px-4 py-2">
                <div className="flex flex-wrap items-end justify-between gap-1 gap-x-3 text-xs text-slate-600">
                  <span className="font-semibold text-slate-800 tabular-nums shrink-0">
                    {isUsHost ? `Step ${currentStep} of ${total}` : `Paso ${currentStep} de ${total}`}
                  </span>
                  <span className="text-slate-700 truncate text-right font-medium min-w-0 flex-1">
                    {stepTitle || ""}
                  </span>
                </div>
                <div
                  className="mt-2 h-2.5 w-full rounded-full bg-slate-200 overflow-hidden"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={isUsHost ? "Visit progress" : "Progreso de la consulta"}
                >
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${atEnd ? "bg-emerald-500" : "bg-sky-600"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-1 text-[11px] text-slate-500 tabular-nums">
                  {pct}% {isUsHost ? "completed" : "completado"}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Step navigation bar */}
        {!isUsHost && (
        <div className="border-t bg-slate-50 no-print wizard-sidebar">
          <ScrollArea className="w-full">
            <div className="flex px-4 py-2 gap-1 min-w-max">
              {WORKFLOW_STEPS.filter((step) => !freezeLatamScaleCapture || step.id !== 5).map(step => {
                const isActive = currentStep === step.id;
                const isDone = currentStep > step.id;
                const Icon = step.icon;
                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(step.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-colors font-medium ${
                      isActive ? "bg-sky-600 text-white" :
                      isDone ? "bg-sky-100 text-sky-700 hover:bg-sky-200" :
                      "bg-white text-slate-500 hover:bg-slate-100 border"
                    }`}
                  >
                    <span className={`flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${isActive ? "bg-white/20" : isDone ? "bg-sky-200" : "bg-slate-100"}`}>
                      {isDone ? <Check className="h-3 w-3" /> : step.id}
                    </span>
                    <span className="hidden sm:inline">{step.label}</span>
                    <Icon className="h-3.5 w-3.5 sm:hidden" />
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
        )}
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {isDemoUser && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 no-print">
            ✨ Modo demo — Puedes explorar el flujo completo de una consulta geriátrica. Para guardar consultas reales <a href="/register" className="font-semibold underline">crea tu cuenta gratis</a>.
          </div>
        )}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              {(() => { const step = WORKFLOW_STEPS.find(s => s.id === currentStep); const Icon = step?.icon || User; return <div className="p-2 bg-sky-100 rounded-lg"><Icon className="h-5 w-5 text-sky-600" /></div>; })()}
              <div>
                <CardTitle className="text-base">
                  {isUsHost
                    ? `${currentUsGuidedBlock}`
                    : `Paso ${currentStep} de ${WORKFLOW_STEPS.length} — ${WORKFLOW_STEPS.find(s => s.id === currentStep)?.label}`}
                </CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  {isUsHost
                    ? "Structured workflow to capture actionable geriatric findings."
                    : WORKFLOW_STEPS.find(s => s.id === currentStep)?.description}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            {!isUsHost && formData.tipo_consulta === "Reconsulta" && currentStep >= 2 && (() => {
              const scaleIds = [
                "barthel",
                "mna_sf",
                "mmse",
                "moca",
                "mini_cog",
                "cam",
                "pfeiffer",
                "yesavage",
                "phq9",
                "gad7",
                "morse",
                "tinetti",
                "sarc_f",
                "tug",
                "polifarmacia_simple",
              ];
              const currentScales = {};
              scaleIds.forEach(id => {
                const s = calculateScoreFromAnswers(id, scaleAnswers[id]);
                if (s != null) currentScales[id] = s;
              });
              return (
                <PreviousConsultationPanel context={followUpContext} formData={formData} currentScales={currentScales} />
              );
            })()}
            <div className="wizard-step-content">
              {renderStepContent()}
            </div>
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-4 no-print wizard-nav-buttons" data-aurora-tour="consulta-nav">
          <Button
            variant="outline"
            onClick={() =>
              goToStep(freezeLatamScaleCapture ? previousLatamWorkflowStep(currentStep) : currentStep - 1)
            }
            disabled={currentStep === 1}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> {isUsHost ? "Previous" : "Anterior"}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrintStep} className="gap-2">
              <Printer className="h-4 w-4" />
              {isUsHost ? "Print" : "Imprimir"}
            </Button>
            <Button variant="outline" onClick={() => handleSaveDraft(true)} disabled={saving || !formData.paciente_id} className="gap-2">
              <Save className="h-4 w-4" /> {isUsHost ? "Save Draft" : "Guardar borrador"}
            </Button>
            {currentStep < WORKFLOW_STEPS.length && (
              <>
                {OPTIONAL_WORKFLOW_STEP_IDS.has(currentStep) && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSaveAndContinue}
                    disabled={saving}
                    className="gap-2"
                  >
                    {isUsHost ? "Skip →" : "Omitir →"}
                  </Button>
                )}
                <Button onClick={handleSaveAndContinue} disabled={saving} className="gap-2 bg-sky-600 hover:bg-sky-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {isUsHost ? "Continue" : "Continuar"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
